import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../../utils/api';
import { fmtDateTime } from '../../../utils/region';
import OverflowChips, { OChip } from '../../OverflowChips';
import Pagination, { getPageSize } from '../../Pagination';
import DlBtn from '../../DlBtn';
import Fab from '../../Fab';
import { guardLeave } from '../../../utils/unsaved';
import type {
  AwBankAccounts, AwBankAcct, AwBankAccts, AwBankCandidate, AwBankCandidates, AwBankLine, AwBankLinesPage, AwBankPayrollHolder, AwBankReconciliation,
  AwBankStateFilter, AwBankStatement, AwBankStatementPage, AwBankTransferConfirmed, AwBankTransferSuggestion, AwBankTransfers, AwBankUploadResult,
  AwPayableDetail,
} from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, baht, btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pill, pillAmber, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';
import { Dialog, Loader, Stat, actBtn, cancelBtn, chip, chipOn, emptyLine, field, lbl, r2, todayIso, useDialogClose } from '../dialog';
import { isReadOnlySeat } from './fixedAssets/shared';
import { DocEntriesBtn } from '../docEntries';
import { epayApi, EPAY_ERRORS } from '../../../utils/api/expensePayments';
import type { EpayReversibility } from '../../../utils/api/expensePayments';
// ⛔ A deliberate import cycle, the house's (Purchases.tsx ↔ ExpenseRecords.tsx): the payment register's one dialog and
// the reason card are Purchases' (batch 11 R5); every cross-reference is inside a component body.
import { PaymentDialog, ReasonDialog } from './Purchases';
import { ReverseEntryBtn } from '../reverseEntryBtn';   // batch 11 R3: "Reverse JV-…" opens the line's entries panel
// Batch 11 W3 R6 (P6c): Generate's report worded by code, the month's hand vouchers (SettleAckDialog), the pairing lock.
import { settleApi, SETTLE_ACK_ERRORS, SETTLE_DIRECTIONS, SETTLE_SKIP_CODES, closedOf } from '../../../utils/api/settlements';
import type { ManualCandidate, SettleBox, SettleCandidates, SettleReport, SettleSkip, SettleSourceRef } from '../../../utils/api/settlements';
// Batch 11 W4 R7 (P7b): "Top up petty cash" in the match dialog (cashApi.topUp) and its refusals' words (cashErr).
import { cashApi, CASH_ERRORS } from '../../../utils/api/clientCash';
import type { TopUpOption } from '../../../utils/api/clientCash';
import type { EpaySettle } from '../../../utils/api/expensePayments';
import { EntryDetailDialog } from '../JournalBook';
import { AttachmentsBtn, useAttachmentCounts } from '../attachments';   // owner 2026-11-02: a statement line's supporting files
import { statutoryBookName } from '../../../utils/countryFeatures';
// ⛔ The house's deliberate cycle once more (Purchases.tsx ↔ ExpenseRecords.tsx ↔ this file): a bill_not_booked line's
// way on opens the bill's own record window — where Book is — over this pane. Used inside a component body only.
import { ExpenseRecordWindow } from './ExpenseRecords';

// ── Banking › Bank accounts (owner 2026-10-13) ──────────────────────────────────────────────────
// Three different things that all answer "which bank accounts does this company have", kept apart
// because they are different records: the accounts the company prints on its own invoices (its
// portal profile), the single account on the firm's client record, and the account numbers that have
// a statutory bank book. The firm's own bank_transactions are the FIRM's money and are not here.
export function BankAccountsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [d, setD] = useState<AwBankAccounts | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankAccounts(ctx.clientId).then((x: AwBankAccounts) => { if (alive) setD(x); }).catch((e: any) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  if (err) return <div style={errBox}>{err}</div>;
  if (!d) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  const empty = <div style={note}>{t('aw_bank_none')}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={sectionHead}>{t('aw_bank_merchant')}</div>
        <div style={card}>
          {!d.merchant?.length ? empty : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_bank_type')}</th><th style={th}>{t('aw_bank_bankname')}</th><th style={th}>{t('aw_bank_name')}</th><th style={th}>{t('aw_bank_number')}</th><th style={th}>{t('aw_bank_swift')}</th><th style={th}>{t('aw_col_currency')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_latest_balance')}</th></tr></thead>
              <tbody>{d.merchant.map((a, i) => (
                <tr key={i}><td style={td}>{a.account_type}</td><td style={td}>{a.bank_name}</td><td style={td}>{a.account_name}</td><td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{a.account_number}</td><td style={td}>{a.swift_code}</td><td style={td}>{a.currency}</td>
                  {/* The newest imported statement line's own running balance, with the day it is
                      from: a balance with no date would read as "now" when the last upload was in
                      March. Nothing is closed to produce it. */}
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {a.latest_balance === null || a.latest_balance === undefined ? <span style={{ color: 'var(--text-tertiary)' }}>{t('aw_bank_rc_no_statement')}</span> : (
                      <>{amt(a.latest_balance)}<div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('aw_bank_rc_as_of')} {dmy(a.latest_balance_date)}</div></>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_bank_client')}</div>
        <div style={card}>
          {!d.client || !(d.client.bank_account_no || d.client.bank_name) ? empty : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_bank_bankname')}</th><th style={th}>{t('aw_bank_branch')}</th><th style={th}>{t('aw_bank_name')}</th><th style={th}>{t('aw_bank_number')}</th></tr></thead>
              <tbody><tr><td style={td}>{d.client.bank_name}</td><td style={td}>{d.client.bank_branch}</td><td style={td}>{d.client.bank_account_name}</td><td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{d.client.bank_account_no}</td></tr></tbody>
            </table>
          )}
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_bank_books')}</div>
        <div style={card}>
          {!d.books?.length ? empty : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_bank_name')}</th><th style={th}>{t('aw_bank_number')}</th><th style={th}>{t('aw_col_currency')}</th></tr></thead>
              <tbody>{d.books.map(b => (
                <tr key={b.id}><td style={td}>{b.name}</td><td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{b.account_number}</td><td style={td}>{b.currency}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Banking › Transactions · Reconciliation · Transfers (owner 2026-10-13, batch 2) ─────────────
// The CLIENT COMPANY's own bank statements, read from merchant_bank_statements / merchant_bank_lines
// through /api/acct-workspace/bank/* — the firm's bank_transactions are never touched or shown.
// Matching is a suggestion the accountant confirms, and confirming only MARKS the statement line
// (owner's announced rule): no journal entry, no receipt, no change to the document's paid figure or
// status. A PDF password is typed for one upload, sent with that request alone, and kept nowhere —
// not in state after the answer, not in the URL, never echoed by the server.

const STATES: AwBankStateFilter[] = ['all', 'unmatched', 'matched', 'ignored', 'transfer'];
// 'payment' is what a line carries once a supplier bill was paid from it (services/merchantPurchases.ts
// recordPayment writes match_kind 'payment' — the candidate was offered as 'payable'); without its own
// label the pill printed the raw token in every language (finance review 2026-10-13). 'reimbursement'
// is batch 4's register: offered as a candidate for a money-out line (approved, unpaid, equal total) and
// carried as match_kind once paid from it — the same token both ways, so one label serves both.
const KIND_KEY: Record<string, string> = {
  invoice: 'aw_bank_kind_invoice', quotation: 'aw_bank_kind_quotation', receipt: 'aw_bank_kind_receipt',
  payable: 'aw_bank_kind_payable', payment: 'aw_bank_kind_payment', reimbursement: 'aw_bank_kind_reimbursement', transfer: 'aw_bank_kind_transfer',
  expense_payment: 'aw_epay_kind',   // batch 11 R5: a recorded payment waiting for its statement line
};
/** A refusal of the payment register v2 (batch 11 R5) by its code; anything else as readErr words it. */
const epayErr = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  return (EPAY_ERRORS as readonly string[]).includes(code) ? t(`aw_epay_err_${code}`) : readErr(e, t);
};
/** (Batch 11 W4 R7) A refusal of the petty cash doors (top-up, its undo) by its code (aw_pc_err_<code>); anything else as
 *  readErr words it. */
const topUpErr = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  return (CASH_ERRORS as readonly string[]).includes(code) ? t(`aw_pc_err_${code}`) : readErr(e, t);
};
// Transfers wear blue so the three marked states read apart at a glance: green = matched to a
// document, blue = the company's own money moving between its accounts, amber = set aside on purpose.
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
// The filter chips, field styles, loader, stat card and Dialog live in ../dialog (shared with Purchases).
type TF = (key: string, opts?: Record<string, unknown>) => string;
const acctLabel = (a: AwBankAcct) => [a.bank_name, a.account_number].filter(Boolean).join(' ') || a.account_name || a.id;
/** The statement's opening row is the one line with neither debit nor credit (the ingest's opening-row
 *  rule): it is a balance, not a movement, so it is never matched, ignored or paired as a transfer. */
const isOpening = (l: AwBankLine) => !Number(l.debit) && !Number(l.credit);
const lineAmount = (l: AwBankLine) => r2(Number(l.credit) || Number(l.debit) || 0);
// An 'invoice' — the match door's word for whatever a credit settles in `invoices` — is named by the
// document's OWN type when the server says it (receipts round 4: a debit note is a candidate and a match
// target, and its Type read "Invoice" beside DN-…). The workspace's type words (aw_dt_*); a bill reads as before.
const DOC_TYPE_KEY: Record<string, string> = {
  invoice: 'aw_dt_invoice', proforma: 'aw_dt_proforma', tax_invoice: 'aw_dt_tax_invoice', tax_invoice_receipt: 'aw_dt_tax_invoice_receipt',
  credit_note: 'aw_dt_credit_note', debit_note: 'aw_dt_debit_note',
};
const kindLabel = (t: TF, kind: string, docType?: string) =>
  kind === 'invoice' && docType && DOC_TYPE_KEY[docType] ? t(DOC_TYPE_KEY[docType]) : KIND_KEY[kind] ? t(KIND_KEY[kind]) : kind;
/** The route's error codes (SPEC-B2 §3) turned into a sentence. The two facts a refusal can carry —
 *  the account number printed in the file and the one chosen — are named through i18n labels; the
 *  server's own English `message` is not repeated after the translated sentence. */
const readErr = (e: any, t: TF): string => {
  const code = String(e?.message || '');
  const p = e?.payload && typeof e.payload === 'object' ? e.payload : {};
  const facts = [
    p.accountNumber ? `${t('aw_bank_err_file_account')}: ${p.accountNumber}` : '',
    p.expectedAccountNumber ? `${t('aw_bank_err_chosen_account')}: ${p.expectedAccountNumber}` : '',
  ].filter(Boolean).join(' · ');
  const tail = facts ? ` (${facts})` : '';
  if (code === 'account_not_registered') return t('aw_bank_err_not_registered') + tail;
  if (code === 'statement_account_mismatch') return t('aw_bank_err_mismatch') + tail;
  if (/has matched lines/i.test(code)) return t('aw_bank_err_has_matched');
  if (e?.status === 409) return `${t('aw_bank_err_conflict')}${code ? ` — ${code}` : ''}`;
  return code || t('aw_bank_err_generic');
};

/** Cancel / Close inside a Dialog, routed through the SHELL's close gate (dialog.tsx useCloseGate),
 *  so this exit asks the same question the X, Esc and the backdrop ask — once, and in one place.
 *  ⛔ A COMPONENT, not a `useDialogClose()` in the dialog's own body: the gate arrives by context
 *  from INSIDE the Dialog's tree, which a hook in the component that RENDERS the Dialog cannot see
 *  (dialog.tsx's own Foot is a component for exactly that reason). The label stays the caller's —
 *  Upload says "Close" once the statement is in, and that distinction is worth keeping.
 *  ⛔ It never saves and it never discards by itself: it only calls the gate. */
const GateCancel = ({ onClose, busy, label }: { onClose: () => void; busy?: boolean; label: string }) => {
  const close = useDialogClose(onClose);
  return <button onClick={close} disabled={busy} style={cancelBtn}>{label}</button>;
};

/** The statutory names stay Thai in every language (the R6 house rule), so they are written here, never in i18n. */
const REMIT_KIND_WORD: Record<string, string> = { pnd1: 'ภ.ง.ด.1', sso: 'สปส.' };
const mmYyyy = (year: number, month: number) => `${String(month).padStart(2, '0')}/${year}`;

/**
 * A line payroll holds (batch 10 R8, §3.4.3 银行那一侧): a payroll payment, or the month's PND.1 and SSO remittances,
 * paid by this one statement line. The holders are the SERVER's (`l.payroll`, read from the payroll tables), never
 * guessed from the mark: a hand-typed "payroll" note is not a holder, and such a line keeps its ordinary pill.
 *
 * ⛔ A BUTTON ONLY WHERE THE SEAT CAN GO THERE (the R7-14 precedent): staff jump to the run window (a payment) or to
 * the remittance in Payroll › Payroll list (prview / remit, R8-20) through ctx.setNav — the shell's guardLeave, one
 * question when a window has typing. A read-only seat (a client's books login, the auditor) never reaches Payroll,
 * so it reads the same words as text. ⛔ setNav clears the keys the other target uses, so a payment's chip cannot
 * land on a remittance left open in the URL, and the other way round.
 */
type PayHolder = Extract<AwBankPayrollHolder, { kind: 'payment' }>;
type RemitHolder = Extract<AwBankPayrollHolder, { kind: 'remittance' }>;
function PayrollPill({ ctx, l, who }: { ctx: AwCtx; l: AwBankLine; who: string }) {
  const { t } = useTranslation();
  const holders = l.payroll?.holders || [];
  const pay = holders.find((h): h is PayHolder => h.kind === 'payment');
  const remits = holders.filter((h): h is RemitHolder => h.kind === 'remittance');
  // A statement line pays exactly one POST (R8-5): one payment, or the one or two remittances of one month.
  const label = pay
    ? t('aw_bank_payroll_pay', { docNo: pay.docNo, payNo: pay.payNo })
    : t('aw_bank_payroll_remit', {
      kinds: remits.map(h => REMIT_KIND_WORD[h.remitKind] || h.remitKind).join(' + '),
      month: remits[0] ? mmYyyy(remits[0].year, remits[0].month) : '',
    });
  const tag = pay ? 'payment' : 'remittance';
  if (isReadOnlySeat(ctx)) return <span style={pillGreen} title={who || undefined} data-bank-payroll={tag}><i className="ti ti-users" aria-hidden /> {label}</span>;
  // The first holder the server lists (PND.1 before SSO) is the remittance the chip opens.
  const go = () => (pay
    ? ctx.setNav('payroll', 'payroll_list', { run: pay.runId, prview: '', remit: '' })
    : ctx.setNav('payroll', 'payroll_list', { prview: 'remittances', remit: String(remits[0]?.id || ''), run: '' }));
  return (
    <button type="button" onClick={go} title={who || undefined} data-bank-payroll={tag}
      style={{ ...pillGreen, cursor: 'pointer', fontFamily: 'inherit' }}>
      <i className="ti ti-users" aria-hidden /> {label}
    </button>
  );
}

function MatchPill({ ctx, l }: { ctx: AwCtx; l: AwBankLine }) {
  const { t } = useTranslation();
  // An opening line carries no money: the resolver would call a baseline "unmatched", so it gets no entries button.
  if (isOpening(l)) return <span style={pillGrey}>{t('aw_bank_opening')}</span>;
  const who = l.matched_by_name ? `${l.matched_by_name}${l.matched_at ? ` · ${fmtDateTime(l.matched_at)}` : ''}` : '';
  // Batch 11 R8a (design §3.6.1): what a line did to the books — its settlement, a transfer partner's, and who holds it —
  // in the document entries panel, asked by the line itself. (Batch 12 tail T5, COA-D2) On EVERY line with money, for
  // every seat: matched, paired, a top-up, ignored, unmatched and payroll-held alike — offered per kind, never per row
  // (docEntries.tsx DocEntriesBtn): whether this line has anything in the books is the resolver's answer, and its panel
  // says so plainly (not matched yet / marked as explaining itself / held by payroll), never a second copy of its rule here.
  const entries = <DocEntriesBtn ctx={ctx} kind="bank_line" id={l.id} docNo={`${dmy(l.txn_date)} ${l.description || ''}`.trim()}
    label={<i className="ti ti-book-2" aria-hidden />} style={{ ...actBtn, padding: '1px 6px', marginLeft: 6 }} />;
  if (l.payroll?.holders?.length) return <span style={{ whiteSpace: 'nowrap' }}><PayrollPill ctx={ctx} l={l} who={who} />{entries}</span>;
  // (Batch 11 W4 R7) A top-up: the box its money went into, read from the line's holder (its live in row), never the mark.
  if (l.topup) {
    return <span style={{ whiteSpace: 'nowrap' }}><span style={pillBlue} title={who || undefined} data-bank-topup={l.topup.boxId}><i className="ti ti-cash" aria-hidden /> {t('aw_topup_pill', { name: l.topup.boxName })} · {amt(l.match_amount)}</span>{entries}</span>;
  }
  switch (l.match_state) {
    case 'matched':
      return <span style={{ whiteSpace: 'nowrap' }}><span style={pillGreen} title={[who, l.match_note].filter(Boolean).join(' — ') || undefined}>{kindLabel(t, l.match_kind, l.match_doc_type)} · {l.match_doc_no} · {amt(l.match_amount)}</span>{entries}</span>;
    case 'transfer':
      return <span style={{ whiteSpace: 'nowrap' }}><span style={pillBlue} title={who || undefined}>{t('aw_bank_kind_transfer')}</span>{entries}</span>;
    case 'ignored':
      // The reason is on hover — the row stays compact, the decision stays explained.
      return <span style={{ whiteSpace: 'nowrap' }}><span style={pillAmber} title={[l.match_note, who].filter(Boolean).join(' — ') || undefined}>{t('aw_bank_state_ignored')}</span>{entries}</span>;
    default:
      return <span style={{ whiteSpace: 'nowrap' }}><span style={pillGrey}>{t('aw_bank_state_unmatched')}</span>{entries}</span>;
  }
}

// ── Upload dialog ───────────────────────────────────────────────────────────────────────────────
function UploadDialog({ ctx, accounts, defaultAccountId, onClose, onUploaded }: {
  ctx: AwCtx; accounts: AwBankAcct[]; defaultAccountId: string; onClose: () => void; onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [accountId, setAccountId] = useState(defaultAccountId);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<AwBankUploadResult | null>(null);
  const [inputKey, setInputKey] = useState(0);   // remounts the file input so the same file can be chosen again
  // FS-12. The flag the shell's close gate reads: set by the EDIT PATH (a file chosen, the account
  // changed, a password typed), never derived by comparing the fields to what they opened with — a
  // dialog that compares itself to its initial state reads dirty the moment anything prefills it,
  // and an operator warned when they typed nothing learns to click the warning away.
  const [dirty, setDirty] = useState(false);
  const submit = async () => {
    if (!file || busy) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const r: AwBankUploadResult = await api.awBankStatementUpload(ctx.clientId, file, { accountId: accountId || undefined, password: password || undefined });
      // The statement is IN: there is nothing unsaved left to lose, so the next close just closes.
      setResult(r); setFile(null); setInputKey(k => k + 1); setDirty(false); onUploaded();
    } catch (e: any) { setErr(readErr(e, t)); }
    // Whatever the outcome, the typed password is dropped the moment the request is answered.
    finally { setPassword(''); setBusy(false); }
  };
  // (Batch 12 tail T2, FS-15) Full screen like every Banking window, its Close / Upload pinned in the footer; the window's
  // "document" is the account the statement goes into (none shown while the server is left to detect it).
  const acct = accounts.find(a => a.id === accountId);
  return (
    <Dialog fullscreen title={t('aw_bank_upload')} docNo={acct ? acctLabel(acct) : undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={result ? t('aw_bank_close') : t('aw_bank_cancel')} />
          <button onClick={submit} disabled={!file || busy} style={{ ...btnPrimary, opacity: !file || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-upload'} aria-hidden /> {t('aw_bank_upload_btn')}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div>
          <label style={lbl}>{t('aw_bank_file')}</label>
          <input key={inputKey} type="file" accept=".pdf,.csv,application/pdf,text/csv" onChange={e => { setDirty(true); setFile(e.target.files?.[0] || null); }} style={{ fontSize: 12.5 }} />
          <div style={note}>{t('aw_bank_file_hint')}</div>
        </div>
        <div>
          <label style={lbl}>{t('aw_bank_account')}</label>
          <select value={accountId} onChange={e => { setDirty(true); setAccountId(e.target.value); }} style={field}>
            <option value="">{t('aw_bank_auto_detect')}</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{acctLabel(a)}{a.currency ? ` · ${a.currency}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>{t('aw_bank_pw')}</label>
          <input type="password" value={password} onChange={e => { setDirty(true); setPassword(e.target.value); }} autoComplete="new-password" style={field} />
          <div style={note}><i className="ti ti-lock" aria-hidden /> {t('aw_bank_pw_hint')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        {result && (
          <div style={{ ...card, background: 'var(--accent-bg)', borderColor: 'var(--accent)', fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700 }}>{t('aw_bank_upload_done', { added: result.added, skipped: result.skipped, rows: result.rows })}</div>
            <div>{result.accountNumber}{result.periodFrom || result.periodTo ? ` · ${dmy(result.periodFrom)} – ${dmy(result.periodTo)}` : ''}{result.closing !== null && result.closing !== undefined ? ` · ${t('aw_bank_closing')}: ${amt(result.closing)}` : ''}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── Manual line dialog (the Fab) ────────────────────────────────────────────────────────────────
function ManualLineDialog({ ctx, accounts, defaultAccountId, onClose, onSaved }: {
  ctx: AwCtx; accounts: AwBankAcct[]; defaultAccountId: string; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [accountId, setAccountId] = useState(defaultAccountId || accounts[0]?.id || '');
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState('');
  const [description, setDescription] = useState('');
  const [dir, setDir] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState('');
  const [channel, setChannel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — see UploadDialog: the edit path sets it, nothing else does. The date opens on TODAY and
  // that is not an edit: a dialog opened and closed without a keystroke must not ask.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const n = Number(amount);
  const ready = !!accountId && /^\d{4}-\d{2}-\d{2}$/.test(date) && !!description.trim() && Number.isFinite(n) && n > 0;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awBankLineAdd(ctx.clientId, {
        accountId, txn_date: date, txn_time: time || undefined, description: description.trim(),
        debit: dir === 'debit' ? r2(n) : 0, credit: dir === 'credit' ? r2(n) : 0,
        balance: balance.trim() === '' ? undefined : Number(balance), channel: channel.trim() || undefined,
      });
      onSaved();
    } catch (e: any) { setErr(readErr(e, t)); setBusy(false); }
  };
  const seg = (on: boolean): React.CSSProperties => ({ ...btn, flex: 1, justifyContent: 'center', ...(on ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) });
  // (Batch 12 tail T2, FS-15) Full screen, its Save pinned in the footer. A line typed by hand has no number of its own.
  return (
    <Dialog fullscreen title={t('aw_bank_add_line')} onClose={onClose} busy={busy} dirty={dirty}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={t('aw_bank_cancel')} />
          <button onClick={submit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-plus'} aria-hidden /> {t('aw_bank_save')}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div style={note}>{t('aw_bank_manual_hint')}</div>
        <div>
          <label style={lbl}>{t('aw_bank_account')}</label>
          <select value={accountId} onChange={e => ed(setAccountId, e.target.value)} style={field}>
            {accounts.map(a => <option key={a.id} value={a.id}>{acctLabel(a)}{a.currency ? ` · ${a.currency}` : ''}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>{t('aw_col_date')}</label><input type="date" value={date} onChange={e => ed(setDate, e.target.value)} style={field} /></div>
          <div><label style={lbl}>{t('aw_bank_time')}</label><input type="time" value={time} onChange={e => ed(setTime, e.target.value)} style={field} /></div>
        </div>
        <div><label style={lbl}>{t('aw_bank_description')}</label><input value={description} onChange={e => ed(setDescription, e.target.value)} style={field} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={lbl}>{t('aw_bank_direction')}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => ed(setDir, 'credit')} style={seg(dir === 'credit')}>{t('aw_bank_credit')}</button>
              <button type="button" onClick={() => ed(setDir, 'debit')} style={seg(dir === 'debit')}>{t('aw_bank_debit')}</button>
            </div>
          </div>
          <div><label style={lbl}>{t('aw_bank_amount')}</label><input type="number" step="0.01" min="0" value={amount} onChange={e => ed(setAmount, e.target.value)} style={field} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>{t('aw_bank_balance_opt')}</label><input type="number" step="0.01" value={balance} onChange={e => ed(setBalance, e.target.value)} style={field} /></div>
          <div><label style={lbl}>{t('aw_bank_channel_opt')}</label><input value={channel} onChange={e => ed(setChannel, e.target.value)} style={field} /></div>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Match dialog ────────────────────────────────────────────────────────────────────────────────
function MatchDialog({ ctx, line, onClose, onDone }: { ctx: AwCtx; line: AwBankLine; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwBankCandidates | null>(null);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState<AwBankCandidate | null>(null);
  const full = lineAmount(line);
  const [amount, setAmount] = useState(String(full));
  const [noteTxt, setNoteTxt] = useState('');
  const [busy, setBusy] = useState(false);
  // A supplier bill is paid from this line through the payment register's dialog (batch 11 R5): its WHT, several bills
  // at once, the whole line — so a bill candidate opens that dialog over this one instead of being matched here.
  const [payBill, setPayBill] = useState('');
  // (Batch 11 W4 R7, P7b task 4) "Top up petty cash": the boxes the SERVER says this debit line may top up (active, opened
  // by its date, the books' currency, no closed period — `topups`) — one click each, one request, never a refused one.
  const [topping, setTopping] = useState('');
  const topInFlight = useRef(false);
  const topUp = async (b: TopUpOption) => {
    if (topInFlight.current || busy) return;
    topInFlight.current = true; setTopping(b.boxId); setErr('');
    try { await cashApi.topUp(ctx.clientId, b.boxId, line.id); onDone(); }
    catch (e: any) { setErr(topUpErr(e, t)); }
    finally { topInFlight.current = false; setTopping(''); }
  };
  // FS-12 — see UploadDialog. The amount opens at the LINE's own amount, which is given, not typed:
  // opening this window and closing it again asks nothing. Picking a candidate does count as work —
  // it is the decision this window exists to make.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    api.awBankCandidates(ctx.clientId, line.id).then((d: AwBankCandidates) => { if (alive) setData(d); }).catch((e: any) => { if (alive) { setData({ line, candidates: [] }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, line.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const exact = (c: AwBankCandidate) => r2(Number(c.amount) || 0) === full;
  // Server order is score DESC then date DESC; exact-amount rows are lifted to the top (stable sort keeps the rest as served).
  const sorted = useMemo(() => [...(data?.candidates || [])].sort((a, b) => Number(exact(b)) - Number(exact(a))), [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const n = Number(amount);
  // A reimbursement is paid whole (SPEC-B4 §2: the line must equal the claim's total, 400 otherwise), so
  // once one is picked the amount is pinned to the line and the field is locked — the server's refusal
  // is never sent. Every other kind keeps the part-amount door open.
  const fullOnly = pick?.kind === 'reimbursement';
  // A recorded payment waiting for its line is attached WHOLE (batch 11 R5): no amount to type.
  const attach = pick?.kind === 'expense_payment';
  const ready = !!pick && (attach || (Number.isFinite(n) && n > 0 && r2(n) <= full && (!fullOnly || r2(n) === full)));
  // Picking a document leaves the amount at the LINE amount (SPEC-B2 §5): a line settling several
  // papers is marked for the one chosen by typing the part that belongs to it — the field is right there.
  // A bill (batch 11 R5) is not picked here: it opens the payment dialog with this line.
  // (Batch 11 W4 P9, W3.1 low) A candidate the match door refuses for the customer's withholding is not chosen (its row says why).
  const choose = (c: AwBankCandidate) => { if (c.prepaidRefusal) return; setDirty(true); if (c.kind === 'payable') { setPayBill(c.id); return; } setPick(c); setAmount(String(full)); };
  const submit = async () => {
    if (!ready || !pick || busy) return;
    setBusy(true); setErr('');
    try {
      if (attach) await epayApi.attachLine(ctx.clientId, pick.id, line.id);
      else await api.awBankMatch(ctx.clientId, line.id, { kind: pick.kind, docId: pick.id, amount: r2(n), note: noteTxt.trim() || undefined });
      onDone();
    } catch (e: any) { setErr(attach ? epayErr(e, t) : readErr(e, t)); setBusy(false); }
  };
  const isCredit = Number(line.credit) > 0;
  return (
    <Dialog title={t('aw_bank_match_title')} onClose={onClose} busy={busy} dirty={dirty} fullscreen
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={t('aw_bank_cancel')} />
          <button onClick={submit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-link'} aria-hidden /> {attach && pick ? t('aw_epay_attach_btn', { payNo: pick.doc_no }) : t('aw_bank_confirm_match')}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ whiteSpace: 'nowrap' }}>{dmy(line.txn_date)}{line.txn_time ? ` ${line.txn_time}` : ''}</span>
          <span style={{ fontWeight: 700, flex: '1 1 200px' }}>{line.description}{line.details ? <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}> · {line.details}</span> : null}</span>
          <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{isCredit ? t('aw_bank_credit') : t('aw_bank_debit')} {amt(full)}</span>
        </div>
        <div style={note}>{t('aw_bank_marks_only_note')}</div>
        {!isCredit && !!data?.topups?.length && (
          <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }} data-topup-section>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}><i className="ti ti-cash" aria-hidden /> {t('aw_topup_section')}</div>
            <div style={note}>{t('aw_topup_hint')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.topups.map(b => (
                <button key={b.boxId} type="button" onClick={() => topUp(b)} disabled={!!topping || busy} data-topup-box={b.boxId}
                  style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)', padding: '5px 10px' }}>
                  <i className={topping === b.boxId ? 'ti ti-loader' : 'ti ti-arrow-right'} aria-hidden /> {t('aw_topup_btn', { name: b.name })}
                  <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 6 }}>{t('aw_topup_balance', { amount: `${b.balanceSat < 0 ? '−' : ''}${baht(Math.abs(b.balanceSat))}` })}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={{ ...card, padding: 0, overflowX: 'auto', maxHeight: '45vh', overflowY: 'auto' }}>
          {data === null ? <Loader /> : !sorted.length ? <div style={emptyLine}>{t('aw_bank_no_candidates')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th} />
                  <th style={th}>{t('aw_col_type')}</th>
                  <th style={th}>{t('aw_col_no')}</th>
                  <th style={th}>{t('aw_bank_cand_name')}</th>
                  <th style={th}>{t('aw_col_date')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_cand_amount')}</th>
                  <th style={th}>{t('aw_bank_cand_reason')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(c => {
                  const on = pick?.id === c.id && pick?.kind === c.kind;
                  return (
                    <tr key={`${c.kind}:${c.id}`} onClick={() => choose(c)} style={{ cursor: c.prepaidRefusal ? 'default' : 'pointer', background: on ? 'var(--accent-bg)' : undefined, opacity: c.prepaidRefusal ? 0.6 : 1 }}
                      data-cand={`${c.kind}:${c.id}`} data-cand-prepaid={c.prepaidRefusal ? c.prepaidRefusal.params?.case || 'over' : undefined} title={c.prepaidRefusal?.message || undefined}>
                      <td style={td}>{c.kind === 'payable'
                        ? <button type="button" onClick={e => { e.stopPropagation(); choose(c); }} style={actBtn} title={t('aw_epay_pay_bill_btn')} aria-label={t('aw_epay_pay_bill_btn')}><i className="ti ti-cash" aria-hidden /></button>
                        : <input type="radio" checked={on} disabled={!!c.prepaidRefusal} onChange={() => choose(c)} aria-label={c.doc_no} />}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{kindLabel(t, c.kind, c.doc_type)}</td>
                      <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.doc_no}
                        {!!c.waitingPayNo && <div style={{ ...pillAmber, marginTop: 3, whiteSpace: 'normal' }} data-cand-waiting={c.id}>{t('aw_epay_waiting_chip', { payNo: c.waitingPayNo })}</div>}
                        {!!c.prepaidRefusal && <div style={{ fontSize: 11, fontWeight: 400, color: '#B42318', marginTop: 3, whiteSpace: 'normal', maxWidth: 360 }} data-cand-prepaid-why={c.id}>
                          {c.prepaidRefusal.params?.case === 'unclear'
                            ? t('aw_bank_cand_prepaid_unclear', { docNos: c.prepaidRefusal.params?.docNos || '—', receipts: c.prepaidRefusal.params?.receipts || '—' })
                            : c.prepaidRefusal.params?.case === 'alone'
                            ? t('aw_bank_cand_prepaid_alone', { docNos: c.prepaidRefusal.params?.docNos || '—', receipt: c.prepaidRefusal.params?.receipt || '—',
                              withheld: amt(Number(c.prepaidRefusal.params?.withheld || 0)), mine: amt(Number(c.prepaidRefusal.params?.mine || 0)) })
                            : t('aw_bank_cand_prepaid_over', { docNos: c.prepaidRefusal.params?.docNos || '—', receipt: c.prepaidRefusal.params?.receipt || '—',
                              withheld: amt(Number(c.prepaidRefusal.params?.withheld || 0)), booked: amt(Number(c.prepaidRefusal.params?.booked || 0)), mine: amt(Number(c.prepaidRefusal.params?.mine || 0)) })}
                        </div>}</td>
                      <td style={td}>{c.name}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(c.date)}</td>
                      <td style={num}>{amt(c.amount)}{c.currency ? ` ${c.currency}` : ''} {exact(c) && <span style={{ ...pillGreen, marginLeft: 6 }}>{t('aw_bank_exact')}</span>}</td>
                      <td style={{ ...td, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {(isCredit || fullOnly) && <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10 }}>
          <div>
            <label style={lbl}>{t('aw_bank_amount')}</label>
            <input type="number" step="0.01" min="0" max={full} value={amount} onChange={e => ed(setAmount, e.target.value)} disabled={fullOnly} style={{ ...field, opacity: fullOnly ? 0.7 : 1 }} />
            {Number.isFinite(n) && n > full && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_bank_amount_over', { max: amt(full) })}</div>}
            {fullOnly && <div style={{ ...note, marginTop: 3 }}>{t('aw_rb_match_full_only')}</div>}
          </div>
          <div><label style={lbl}>{t('aw_bank_note_opt')}</label><input value={noteTxt} onChange={e => ed(setNoteTxt, e.target.value)} style={field} /></div>
        </div>}
        {err && <div style={errBox}>{err}</div>}
        {payBill && <PaymentDialog ctx={ctx} bills={[{ id: payBill }]} line={line} onClose={() => setPayBill('')} onDone={onDone} />}
      </div>
    </Dialog>
  );
}

// ── Ignore dialog ───────────────────────────────────────────────────────────────────────────────
function IgnoreDialog({ ctx, line, onClose, onDone }: { ctx: AwCtx; line: AwBankLine; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — one field, and a typed reason is exactly the kind of work a stray backdrop click used
  // to throw away without a word.
  const [dirty, setDirty] = useState(false);
  const ready = !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await api.awBankIgnore(ctx.clientId, line.id, reason.trim()); onDone(); }
    catch (e: any) { setErr(readErr(e, t)); setBusy(false); }
  };
  // (Batch 12 tail T2, FS-15, TL-8) Full screen — a one-reason window included — its Ignore pinned in the footer; the
  // window's "document" is the statement line (its date and description).
  return (
    <Dialog fullscreen title={t('aw_bank_ignore_title')} docNo={`${dmy(line.txn_date)} · ${line.description || ''}`.trim()} onClose={onClose} busy={busy} dirty={dirty}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={t('aw_bank_cancel')} />
          <button onClick={submit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-eye-off'} aria-hidden /> {t('aw_bank_ignore_btn')}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{dmy(line.txn_date)} · <b style={{ color: 'var(--text-primary)' }}>{line.description}</b> · {amt(lineAmount(line))}</div>
        <div>
          <label style={lbl}>{t('aw_bank_ignore_reason')}</label>
          <textarea value={reason} onChange={e => { setDirty(true); setReason(e.target.value); }} rows={3} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{t('aw_bank_ignore_hint')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Undo a top-up (batch 11 W4 R7, P7b task 4; design §3.4, W4-5) ───────────────────────────────────────────────────────
/**
 * "Undo top-up…": the line's money taken back out of the petty cash box — the box's in row reversed WITH this reason and
 * kept (struck, in Banking › Petty Cash), the line unmatched, its draft settlement (if any) discarded and named. The
 * reason is required (Confirm stays off while it is empty) and the request is sent once (a double click sends one). Offered
 * only where the server takes it: never while the line's settlement is in the books (R3's sentence and "Reverse JV-…"
 * stand instead) and never on a line dated in a closed period (the sentence instead). Full screen, the shell's window with
 * its Confirm pinned in the footer; dirty from the first keystroke.
 */
export function TopUpUndoDialog({ ctx, line, onClose, onDone }: { ctx: AwCtx; line: AwBankLine; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const why = reason.trim();
  const ready = !!why && why.length <= 500 && !busy;
  const draft = line.settlement && !line.settlement.inBooks ? line.settlement.docNo : '';
  const submit = async () => {
    if (!ready || inflight.current) return;
    inflight.current = true; setBusy(true); setErr('');
    try { await api.awBankUnmatch(ctx.clientId, line.id, why); setDirty(false); onDone(); }
    catch (e: any) { setErr(topUpErr(e, t)); }
    finally { inflight.current = false; setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_topup_undo_title', { name: line.topup?.boxName || '—' })} docNo={`${dmy(line.txn_date)} · ${amt(line.debit)}`} onClose={onClose} busy={busy} dirty={dirty}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={t('aw_bank_cancel')} />
          <button onClick={submit} disabled={!ready} data-topup-undo-confirm style={{ ...btnPrimary, opacity: ready ? 1 : 0.5 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-arrow-back-up'} aria-hidden /> {t('aw_topup_undo_confirm')}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }} data-topup-undo-dialog={line.id}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{dmy(line.txn_date)} · <b style={{ color: 'var(--text-primary)' }}>{line.description}</b> · {amt(line.debit)}</div>
        {!!draft && <div style={note} data-topup-undo-draft={draft}>{t('aw_topup_undo_drafts', { docNos: draft })}</div>}
        <div>
          <label style={lbl}>{t('aw_topup_undo_reason')}</label>
          <textarea value={reason} onChange={e => { setDirty(true); setReason(e.target.value); }} rows={3} maxLength={500} style={{ ...field, resize: 'vertical' }} data-topup-undo-reason />
          <div style={note}>{t('aw_topup_undo_hint')}</div>
        </div>
        {err && <div style={errBox} data-topup-undo-err>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Generate's words, by code (batch 11 W3 R6, P6c task 1; the W3 brief §4.7) ─────────────────────────────────────────
/** A direction's word (aw_settle_dir_<direction>) — one of the server's directions, else the explicit accounts' word. */
const sideWord = (t: TF, dir: any) => t(`aw_settle_dir_${(SETTLE_DIRECTIONS as readonly string[]).includes(String(dir)) ? String(dir) : 'accounts'}`);
/** 'YYYY-MM' → 'MM/YYYY' (the UK order every month on these screens is written in). */
const ymLabel = (ym: string) => { const [y, m] = String(ym || '').split('-'); return y && m ? `${m}/${y}` : String(ym || ''); };
/** The vouchers a skip names, for its sentence: the first ten of the candidates it carries (each one's number), else the
 *  server's list with its English "and N more" turned into figures. */
const docNosOf = (sk: { params?: Record<string, string>; candidates?: ManualCandidate[] }): string => {
  const c = sk.candidates || [];
  if (c.length) return c.slice(0, 10).map(x => x.docNo || x.entryId.slice(0, 8)).join(', ') + (c.length > 10 ? ` … (+${c.length - 10})` : '');
  return String(sk.params?.docNos || '').replace(/ and (\d+) more$/, ' … (+$1)');
};
/** (W4 R7) A transfer's `unpaired` by which side is not paired (params.side). ⛔ Every key written in full. */
const TRANSFER_SIDE_KEY: Record<string, string> = {
  from: 'aw_settle_skip_unpaired_transfer_from', to: 'aw_settle_skip_unpaired_transfer_to', both: 'aw_settle_skip_unpaired_transfer_both',
};
/**
 * Why Generate left a line, in the reader's language: worded by the skip's CODE (aw_settle_skip_<code>) with the figures
 * its `params` carry, never the server's English — which rides as the hover text, and is said (aw_settle_skip_generic)
 * only for a code this build does not know. ⛔ client_not_vat never says "Book": Book refuses such a client's bills.
 * Exported: the payment history (Purchases.tsx PayablePayments) words the same skips by the same function.
 */
export function settleSkipText(t: TF, sk: { code: string; why: string; params?: Record<string, string>; candidates?: ManualCandidate[] }): string {
  const p = sk.params || {};
  const fig = (v: any) => (v === undefined || v === null || v === '' ? '—' : amt(Number(v)));
  // A code this build does not know (a newer server): the server's own sentence, said as such.
  if (!(SETTLE_SKIP_CODES as readonly string[]).includes(sk.code)) return t('aw_settle_skip_generic', { why: sk.why || sk.code });
  switch (sk.code) {
    case 'no_money': return t('aw_settle_skip_no_money');
    case 'part_matched': return t('aw_settle_skip_part_matched', { matched: fig(p.matched), moved: fig(p.moved) });
    case 'currency': return t('aw_settle_skip_currency', { currency: p.currency || '—', home: p.home || '—' });
    case 'no_rule': return t('aw_settle_skip_no_rule', { kind: p.matchKind ? kindLabel(t, p.matchKind) : '—' });
    case 'holder_gone': return t('aw_settle_skip_holder_gone', { docNo: p.docNo || '—' });
    case 'client_not_vat': return t('aw_settle_skip_client_not_vat');
    case 'bill_not_booked': return t('aw_settle_skip_bill_not_booked', { bills: p.bills || '—' });
    // (W4 R7) A transfer's two accounts: which of them is not paired (params.side from / to / both) — a line's own otherwise.
    case 'unpaired': return TRANSFER_SIDE_KEY[String(p.side || '')] ? t(TRANSFER_SIDE_KEY[String(p.side || '')]) : t('aw_settle_skip_unpaired');
    case 'same_ledger_account': return t('aw_settle_skip_same_ledger_account', { code: p.code || '—' });
    case 'box_unusable': return t('aw_settle_skip_box_unusable');
    case 'no_control': return t('aw_settle_skip_no_control', { control: p.control || '' });
    case 'answer_wht_payable': return t('aw_settle_skip_answer_wht_payable', { role: t('aw_pacc_role_wht_payable'), w: fig(p.w) });
    case 'answer_wht_prepaid': return t('aw_settle_skip_answer_wht_prepaid', { role: t('aw_pacc_role_wht_prepaid'), w: fig(p.w) });
    // (W3.1) A hand entry for a line matched to the receipt's invoice, whose receipts withheld more than once: asked.
    case 'wht_prepaid_unclear': return t('aw_settle_skip_wht_prepaid_unclear', { docNo: p.docNo || '—', invoice: p.invoice || '—', receipts: p.receipts || '—', line: p.line || '—' });
    // (W3.2) The receipt's withholding booked in part by its other entries: the leg is whole or none — by hand with the rest.
    case 'wht_prepaid_part': return t('aw_settle_skip_wht_prepaid_part', { receipt: p.receipt || '—', w: fig(p.w), booked: fig(p.booked), room: fig(p.room) });
    case 'manual_settlement_candidates': return t('aw_settle_skip_manual_settlement_candidates', {
      n: p.n || String((sk.candidates || []).length), month: ymLabel(p.month), docNos: docNosOf(sk),
      side: sideWord(t, p.direction) });
    // (Batch 11 W4 P9, W3 low) A date a closed period covers: the closed form (never "open a period") — record it by hand.
    case 'no_open_period': return p.closedFrom
      ? t('aw_settle_skip_no_open_period_closed', { date: dmy(p.date || ''), from: dmy(p.closedFrom), to: dmy(p.closedTo || '') })
      : t('aw_settle_skip_no_open_period', { date: dmy(p.date || '') });
    case 'not_candidate': return p.docNo ? t('aw_settle_skip_not_candidate_entry', { docNo: p.docNo }) : t('aw_settle_skip_not_candidate');
    default: return t('aw_settle_skip_generic', { why: sk.why || sk.code });
  }
}

/** (Batch 11 W4 R7, P7b task 6) How a report row names its source: a statement line by its match's document number; a petty
 *  cash row as "Petty cash <box> · PMT-…" (aw_settle_src_cash) — the row is the cash payment's, not a bank line. */
export function settleSourceLabel(t: TF, x: { docNo: string; sourceKind?: string; box?: SettleBox }): string {
  if (x.sourceKind === 'cash_line') return t('aw_settle_src_cash', { box: x.box?.name || '—', docNo: x.docNo || '—' });
  return x.docNo || '—';
}

/** The pairing lock's sentence when nothing of it is in a closed period (aw_pair_locked), and — W3.1 — when some or all of
 *  its settlements have a reversal raised already: post those (aw_pair_locked_rev / aw_pair_locked_some_rev), never
 *  "reverse" them (the server's pairingInUseWhy says the same). */
export function pairLockText(t: TF, code: string, inBooks: { entryId: string; docNo: string; reversalDraft?: string }[]): string {
  const no = (e: { entryId: string; docNo: string }) => e.docNo || e.entryId.slice(0, 8);
  const docNos = inBooks.map(no).join(', ');
  const rv = inBooks.filter(e => !!e.reversalDraft), rest = inBooks.filter(e => !e.reversalDraft);
  if (!rv.length) return t('aw_pair_locked', { code, docNos });
  const rvs = rv.map(e => e.reversalDraft).join(', ');
  return rest.length ? t('aw_pair_locked_some_rev', { code, docNos, rvs, others: rest.map(no).join(', ') }) : t('aw_pair_locked_rev', { code, docNos, rvs });
}

/** (W3.1; W3.2) Why a receipt's line is not unmatched for the customer's withholding (GET /bank/lines `prepaidHold`, the
 *  unmatch door's own 409 wht_prepaid_held), in the reader's language by its case; the server's sentence for anything else.
 *  left_here (W3.2): what happened (a settlement of the receipt left the withholding to ANOTHER entry — never "to this
 *  line"), then the way that works now by `gen` — generate this line's settlement; record it by hand (in an open period
 *  when its own date is in a closed one: Generate is certain to be refused there); wait for the draft that books the rest
 *  — and by `way` whether the deferring settlement can be taken out instead (it stays when posted in a closed period). */
export function prepaidHoldText(t: TF, h: { case: string; message: string; params?: Record<string, string> }): string {
  const p = h.params || {};
  if (h.case === 'hand_draft') return t('aw_bank_prepaid_hand_draft', { docNo: p.docNo || '—', receipt: p.receipt || '—' });
  if (h.case === 'left_here') {
    const room = p.room ? amt(Number(p.room)) : '—';
    // (Batch 11 W4 P9, W3.2 low) The draft that books the rest, named on the screen (`waitFor`), not only in the hover.
    const keep = p.gen === 'wait' ? (p.waitFor ? t('aw_bank_prepaid_lh_wait_named', { drafts: p.waitFor }) : t('aw_bank_prepaid_lh_wait'))
      : p.gen === 'hand_closed' ? t('aw_bank_prepaid_lh_hand_closed', { room })
        : p.gen === 'hand' ? t('aw_bank_prepaid_lh_hand', { room }) : t('aw_bank_prepaid_lh_generate');
    const or = p.way === 'closed' ? t('aw_bank_prepaid_lh_stays')
      : p.way === 'post_reversal' ? t('aw_bank_prepaid_lh_or_rv', { rv: p.rev || '—' }) : t('aw_bank_prepaid_lh_or_reverse');
    return `${t('aw_bank_prepaid_left_here', { receipt: p.receipt || '—', line: p.line || '—' })} ${keep} ${or}`;
  }
  return h.message;
}

/** bill_not_booked's way on: the bill's own record window (Expenses › Expense records) — where Book is — opened over this
 *  pane. Its key is the record the bill belongs to (the scan it was made from, else the bill itself), read from the bill
 *  first (GET /payables/:id, a read). Closing it hands back to the caller (the report re-runs its preview). */
export function OpenBillBtn({ ctx, goTo, onClosed }: { ctx: AwCtx; goTo: { kind: string; id: string; docNo: string }; onClosed?: () => void }) {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const open = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const d: AwPayableDetail = await api.awPayable(ctx.clientId, goTo.id);
      const up = String(d?.payable?.expense_upload_id || '');
      setKey(up ? `u:${up}` : `p:${goTo.id}`);
    } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={open} disabled={busy} data-settle-open-bill={goTo.id} style={{ ...actBtn, marginLeft: 6, padding: '1px 7px', fontSize: 11.5 }}>
        <i className={busy ? 'ti ti-loader' : 'ti ti-file-invoice'} aria-hidden /> {t('aw_settle_open_bill', { docNo: goTo.docNo })}
      </button>
      {err && <span style={{ fontSize: 11.5, color: '#B42318', marginLeft: 6 }}>{err}</span>}
      {key && <ExpenseRecordWindow ctx={ctx} recordKey={key} onClose={() => { setKey(''); onClosed?.(); }} />}
    </>
  );
}

/** One skipped line's way on (the W3 brief §4.7's last column) — a place to put it right, never a write from here:
 *  bill_not_booked → the bill's record window (where Book is); the WHT answers → Settings › Posting accounts; unpaired →
 *  the picker above (said); the month's hand vouchers → "Review N vouchers" (SettleAckDialog); holder_gone → Banking ›
 *  Transactions; no_control → the chart; no_open_period → the periods. client_not_vat and the line's own steps: the
 *  sentence alone. Each jump goes through ctx.setNav (guardLeave, one replacing URL write). */
function SkipWayOn({ ctx, x, onReview, onBack }: { ctx: AwCtx; x: SettleSkip; onReview: () => void; onBack: () => void }) {
  const { t } = useTranslation();
  const way: React.CSSProperties = { ...actBtn, marginLeft: 6, padding: '1px 7px', fontSize: 11.5 };
  switch (x.code) {
    case 'bill_not_booked': return x.goTo && x.goTo.id ? <OpenBillBtn ctx={ctx} goTo={x.goTo} onClosed={onBack} /> : null;
    case 'answer_wht_payable': case 'answer_wht_prepaid':
      return <button type="button" style={way} data-settle-way="pacc" onClick={() => ctx.setNav('settings', 'posting_accounts')}><i className="ti ti-settings" aria-hidden /> {t('aw_settle_way_pacc')}</button>;
    // (Batch 11 W4 P9, W3 low) The report lists every account's lines: an unpaired line names ITS account, so the way on says
    // which one to choose above (the reconciliation shows one account at a time).
    case 'unpaired': case 'same_ledger_account': return <span style={{ marginLeft: 6, fontSize: 11.5, color: 'var(--text-secondary)' }} data-settle-way="pair" data-settle-pair-account={x.params?.account || undefined}>
      <i className="ti ti-arrow-up" aria-hidden /> {x.params?.account ? t('aw_settle_way_pair_account', { account: x.params.account }) : t('aw_settle_way_pair_above')}</span>;
    // (W4 R7) A petty cash box's account that cannot take the entry: the box's page, where its account is shown.
    case 'box_unusable': return <button type="button" style={way} data-settle-way="petty_cash" onClick={() => ctx.setNav('banking', 'petty_cash')}><i className="ti ti-cash" aria-hidden /> {t('aw_settle_way_petty_cash')}</button>;
    case 'manual_settlement_candidates': {
      const n = Number(x.params?.n) || (x.candidates || []).length;
      return <button type="button" style={{ ...way, borderColor: 'var(--accent)', color: 'var(--accent-text)' }} data-settle-review={x.lineId} data-settle-review-kind={x.sourceKind || 'bank_line'} onClick={onReview}><i className="ti ti-list-check" aria-hidden /> {t('aw_sack_review', { n })}</button>;
    }
    case 'holder_gone': case 'wht_prepaid_unclear': case 'wht_prepaid_part': return <button type="button" style={way} data-settle-way="transactions" onClick={() => ctx.setNav('banking', 'transactions')}><i className="ti ti-arrows-exchange" aria-hidden /> {t('aw_settle_way_transactions')}</button>;
    case 'no_control': return <button type="button" style={way} data-settle-way="chart" onClick={() => ctx.setNav('accounting', 'chart')}><i className="ti ti-list-tree" aria-hidden /> {t('aw_settle_way_chart')}</button>;
    case 'no_open_period': return x.params?.closedFrom
      ? <span style={{ marginLeft: 6, fontSize: 11.5, color: 'var(--text-secondary)' }} data-settle-way="by_hand"><i className="ti ti-hand-finger" aria-hidden /> {t('aw_settle_way_by_hand')}</span>
      : <button type="button" style={way} data-settle-way="periods" onClick={() => ctx.setNav('accounting', 'periods')}><i className="ti ti-calendar" aria-hidden /> {t('aw_settle_way_periods')}</button>;
    default: return null;
  }
}

/** A refusal of the voucher dialog's door by its code (aw_sack_err_<code>); anything else in the server's words. */
const sackErr = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  return (SETTLE_ACK_ERRORS as readonly string[]).includes(code) ? t(`aw_sack_err_${code}`) : (String(e?.payload?.error || e?.message || '') || t('aw_sack_err_generic'));
};

// ── The month's hand vouchers, confirmed one by one (batch 11 W3 R6, P6c task 2; design §3.3.4, D20, W3-9 / W3-10) ─────
/**
 * Before Generate writes a statement line's settlement, every posted hand voucher of the line's month that moves money
 * the same way (Dr AP / Cr cash for a payment, Dr cash / Cr AR for a receipt) must be confirmed by a person, ONE BY ONE,
 * as NOT this line's settlement — CJT and Big Housekeeper settle by hand every month, and a line generated beside its own
 * hand voucher would be in the books twice, balanced, with nothing else to say so.
 *  · The list is the server's (GET /bank/settlements/candidates, 100 a page): found by account and month only, never by
 *    number or amount. The control-side amount is SHOWN for the person to judge; each voucher opens read-only in the
 *    entry-detail window.
 *  · A voucher already confirmed for this line's holder says who and when, ticked and disabled.
 *  · Confirm sends the ticked ones (POST /bank/settlements/acks) — once: a double click sends one request. A 409 (the
 *    line's holder changed, or a voucher left the list since it was read) reads the list again with the server's
 *    sentence, and never sends a second POST by itself.
 *  · ⛔ A read-only seat reads the list (a GET) — no tick, no Confirm.
 * Full screen (a list of up to 100), through the shell with its dirty guard: a tick is work a stray click would lose.
 * Exported: the payment history (Purchases.tsx) opens it for a payment's line.
 */
export function SettleAckDialog({ ctx, lineId, source, line, onClose, onDone }: {
  ctx: AwCtx;
  /** The statement line the vouchers are asked for (W3's callers). */
  lineId?: string;
  /** (Batch 11 W4 R7) …or the source named with its kind: a statement line, or a petty cash row (a cash payment's out row). */
  source?: SettleSourceRef;
  /** The source, as the header names it: its date and amount. */
  line?: { date: string; amount: number } | null;
  onClose: () => void;
  /** After each Confirm that the server took, with how many of the month's vouchers are still unconfirmed for this line. */
  onDone: (remaining: number) => void;
}) {
  const { t } = useTranslation();
  const ro = isReadOnlySeat(ctx);
  const src: SettleSourceRef = source || String(lineId || '');
  const srcId = typeof src === 'string' ? src : src.id;
  const isRow = typeof src !== 'string' && src.kind === 'cash_line';
  const [data, setData] = useState<SettleCandidates | null>(null);
  const [page, setPage] = useState(1);
  const [reread, setReread] = useState(0);
  const [tick, setTick] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [err, setErr] = useState('');
  const [entryId, setEntryId] = useState('');
  // FS-12: a tick is the work this window exists for; reading the list marks nothing.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    let alive = true;
    settleApi.candidates(ctx.clientId, src, page)
      .then((d: SettleCandidates) => { if (alive) setData(d); })
      .catch((e: any) => { if (alive) { setData({ holder: null, month: '', direction: null, rows: [], total: 0, page: 1, limit: 100, unacknowledged: 0 }); setErr(sackErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, srcId, isRow, page, reread]);   // eslint-disable-line react-hooks/exhaustive-deps
  const rows = data?.rows || [];
  // Every tick, on every page read so far (a month has at most a few dozen; the list pages at 100), less any the server
  // now says is confirmed — never only the page on screen.
  const acked = new Set(rows.filter(r => !!r.ack).map(r => r.entryId));
  const ticked = Object.keys(tick).filter(id => tick[id] && !acked.has(id));
  const ready = !ro && !!ticked.length && !busy;
  const confirm = async () => {
    if (!ready || inFlight.current) return;   // ⛔ in flight: a second click sends nothing
    inFlight.current = true; setBusy(true); setErr('');
    try {
      // (Batch 11 W4 P9, W3 low) The door takes at most 100 ids a request (400 invalid above that): the ticks of every page
      // are sent 100 at a time, in order — never one request bound to be refused.
      let r: any = null;
      for (let i = 0; i < ticked.length; i += 100) r = await settleApi.ack(ctx.clientId, src, ticked.slice(i, i + 100));
      setTick({}); setDirty(false); setReread(n => n + 1);
      onDone((r?.remaining || []).length);
    } catch (e: any) {
      setErr(sackErr(e, t)); setTick({}); setDirty(false); setReread(n => n + 1);
    } finally { inFlight.current = false; setBusy(false); }
  };
  const month = ymLabel(data?.month || '');
  const side = sideWord(t, data?.direction);
  return (
    <Dialog title={t('aw_sack_title', { month })} docNo={data?.holder?.docNo || undefined} onClose={onClose} busy={busy} dirty={dirty} fullscreen
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} busy={busy} label={ro ? t('aw_dlg_close') : t('aw_bank_cancel')} />
          {!ro && (
            <button onClick={confirm} disabled={!ready} data-sack-confirm style={{ ...btnPrimary, opacity: ready ? 1 : 0.5 }}>
              <i className={busy ? 'ti ti-loader' : 'ti ti-checks'} aria-hidden /> {t('aw_sack_confirm')}{ticked.length ? ` (${ticked.length})` : ''}
            </button>
          )}
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-sack-line={srcId} data-sack-kind={isRow ? 'cash_line' : 'bank_line'}>
        <div style={{ ...card, fontSize: 12.5, fontWeight: 600 }} data-sack-head>
          {t(isRow ? 'aw_sack_row' : 'aw_sack_line', { date: line?.date ? dmy(line.date) : '—', amount: line ? amt(line.amount) : '—', holder: data?.holder?.docNo || '—' })}
        </div>
        <div style={note}>{t('aw_sack_month', { month: month || '—', side })}</div>
        {err && <div style={errBox} data-sack-err>{err}</div>}
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_sack_none')}</div> : (
            <table style={table}>
              <thead><tr>
                <th style={th}>{t('aw_sack_col_date')}</th><th style={th}>{t('aw_sack_col_no')}</th><th style={th}>{t('aw_sack_col_book')}</th>
                <th style={th}>{t('aw_sack_col_narration')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_sack_col_amount')}</th>
                <th style={th}>{t('aw_sack_col_tick')}</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.entryId} data-sack-row={r.entryId} data-sack-acked={r.ack ? '1' : undefined}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(r.entryDate)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => setEntryId(r.entryId)} data-sack-open={r.entryId} title={t('aw_sd_open_title')}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                        {r.docNo || r.entryId.slice(0, 8)}
                      </button>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{statutoryBookName(r.book) || r.book || '—'}{r.pageNo != null ? ` / ${r.pageNo}` : ''}</td>
                    <td style={{ ...td, fontSize: 12 }}>{r.narration}</td>
                    <td style={num}>{baht(r.controlSat)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {!ro && (
                        <input type="checkbox" checked={!!r.ack || !!tick[r.entryId]} disabled={!!r.ack || busy} aria-label={t('aw_sack_tick')} title={t('aw_sack_tick')} data-sack-tick={r.entryId}
                          onChange={e => { setDirty(true); const on = e.target.checked; setTick(m => ({ ...m, [r.entryId]: on })); }} />
                      )}
                      {r.ack && <span style={{ fontSize: 11.5, color: '#166534', marginLeft: 6 }} data-sack-by={r.entryId}>{t('aw_sack_confirmed_by', { by: r.ack.byName || '—', at: fmtDateTime(r.ack.at) })}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {data && <div data-sack-pager><Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} /></div>}
        {entryId && <EntryDetailDialog entryId={entryId} onClose={() => setEntryId('')} />}
      </div>
    </Dialog>
  );
}

// ── Statements list (collapsible under the toolbar) ─────────────────────────────────────────────
function StatementsList({ ctx, accountId, onChanged }: { ctx: AwCtx; accountId: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwBankStatementPage | null>(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  // A read-only seat (a client's own books login, the auditor) opens a statement and never withdraws one:
  // the server refuses its every non-GET, and each refusal pages the admins (middleware.ts).
  const ro = isReadOnlySeat(ctx);
  useEffect(() => {
    let alive = true;
    api.awBankStatements(ctx.clientId, { accountId, page, limit: 100 })
      .then((d: AwBankStatementPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit: 100 }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, accountId, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (s: AwBankStatement) => {
    if (busy) return; setBusy(s.id); setErr('');
    try { openBlob(await api.awBankStatementFileBlobUrl(ctx.clientId, s.id)); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const del = async (s: AwBankStatement) => {
    if (busy || !window.confirm(t('aw_bank_stmt_delete_confirm', { name: s.filename }))) return;
    setBusy(s.id); setErr('');
    try { await api.awBankStatementDelete(ctx.clientId, s.id); onChanged(); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const rows = data?.rows || [];
  return (
    <div>
      <div style={sectionHead}>{t('aw_bank_statements')}</div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_bank_no_statements')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_bank_file')}</th><th style={th}>{t('aw_bank_account')}</th><th style={th}>{t('aw_col_period')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_rows')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_added')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_closing')}</th>
                <th style={th}>{t('aw_bank_uploaded_by')}</th><th style={th}>{t('aw_exp_uploaded')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id}>
                  <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.filename}>
                    <i className={s.source === 'csv' ? 'ti ti-file-type-csv' : 'ti ti-file-type-pdf'} style={{ color: 'var(--text-tertiary)' }} aria-hidden /> {s.filename}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{s.account_number}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.period_from || s.period_to ? `${dmy(s.period_from)} – ${dmy(s.period_to)}` : ''}</td>
                  <td style={num}>{s.row_count}</td>
                  <td style={num}>{s.added_count}</td>
                  <td style={num}>{s.closing_balance === null || s.closing_balance === undefined ? '' : amt(s.closing_balance)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.created_by_name} <span style={pillGrey}>{s.uploaded_via === 'portal' ? t('aw_bank_via_portal') : t('aw_bank_via_workspace')}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(s.created_at)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => open(s)} disabled={!!busy} style={{ ...actBtn, marginRight: 6 }}><i className={busy === s.id ? 'ti ti-loader' : 'ti ti-file-search'} aria-hidden /> {t('aw_bank_open')}</button>
                    {!ro && !s.hold && <button onClick={() => del(s)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} title={t('aw_bank_stmt_delete_hint')}><i className="ti ti-trash" aria-hidden /> {t('aw_bank_delete')}</button>}
                    {/* (W2 ship) Why it cannot be withdrawn — said, never a Delete the server refuses. */}
                    {!ro && !!s.hold && (
                      <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', display: 'inline-block', maxWidth: 280, verticalAlign: 'middle' }} data-stmt-held={s.hold.code}>
                        {s.hold.code === 'matched' ? t('aw_bank_stmt_held_matched')
                          : t('aw_bank_stmt_held_books', { docNo: (s.hold.docNos || [])[0] || '' })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />}
    </div>
  );
}

// ── Transactions pane ───────────────────────────────────────────────────────────────────────────
export function TransactionsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  // Reconciliation's "Work the unmatched" — and a colleague's pasted link — hand over ?state=&accountId=.
  // They are read when the pane mounts, then taken off the URL: a filter the accountant changes
  // afterwards must not be undone by the next remount (the shell remounts panes on every period
  // change). The strip waits until the period list has answered: on a deep link the shell may mount
  // this pane before /ledger/periods resolves and remount it right after, and stripping on that first
  // mount would hand the second one an empty URL (reviewer 2026-10-13).
  const [accountId, setAccountId] = useState(() => params.get('accountId') || '');
  const [state, setState] = useState<AwBankStateFilter>(() => { const s = params.get('state') || ''; return (STATES as string[]).includes(s) ? s as AwBankStateFilter : 'all'; });
  useEffect(() => {
    if (ctx.periodsLoading) return;
    if (!params.has('state') && !params.has('accountId')) return;
    setParams(p => { const n = new URLSearchParams(p); n.delete('state'); n.delete('accountId'); return n; }, { replace: true });
  }, [ctx.periodsLoading, params, setParams]);

  const [accounts, setAccounts] = useState<AwBankAcct[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwBankLinesPage | null>(null);
  const clips = useAttachmentCounts(ctx.clientId, 'bank_line', (data?.rows || []).map(l => l.id));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showStatements, setShowStatements] = useState(false);
  const [dlg, setDlg] = useState<{ kind: 'upload' } | { kind: 'manual' } | { kind: 'match'; line: AwBankLine } | { kind: 'ignore'; line: AwBankLine }
    | { kind: 'paySeveral'; line: AwBankLine } | { kind: 'reverse'; line: AwBankLine; r: EpayReversibility } | { kind: 'undoOld'; line: AwBankLine }
    | { kind: 'undoTopup'; line: AwBankLine } | null>(null);
  // Why a payment line cannot be reversed now (batch 11 R5): the reversible pre-check's answer, by line.
  const [lineWhy, setLineWhy] = useState<Record<string, { text: string; entryDocNo?: string }>>({});
  // Every write here goes through ctx.bump, the workspace's one re-read signal (ctx.ts): this pane's
  // own lists and badges re-read on ctx.reload like every sibling pane, and any pane mounted later sees
  // the change too — no pane-local counter beside it (reviewer 2026-10-13).
  const refresh = ctx.bump;
  // ⛔ A read-only seat reads the lines, filters, exports and opens statements — and is drawn none of the
  // writes: no Upload, no +, no Match / Ignore / Delete / Unmatch (the whole Actions column goes).
  const ro = isReadOnlySeat(ctx);

  useEffect(() => {
    let alive = true;
    api.awBankAccts(ctx.clientId).then((d: AwBankAccts) => { if (alive) setAccounts(d.rows || []); }).catch((e: any) => { if (alive) { setAccounts([]); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awBankLines(ctx.clientId, { accountId, from, to, state, q: qd, page, limit })
      .then((d: AwBankLinesPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, accountId, from, to, state, qd, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); refresh(); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const unmatch = (l: AwBankLine) => act(l.id, () => api.awBankUnmatch(ctx.clientId, l.id));
  // A line a batch 11 payment holds is freed by reversing that payment, with its reason (W2-18) — asked first.
  const reverseFor = (l: AwBankLine) => act(l.id, async () => {
    const r = await epayApi.reversible(ctx.clientId, String(l.expense_payment?.id));
    if (r.ok) { setDlg({ kind: 'reverse', line: l, r }); return; }
    setLineWhy(w => ({ ...w, [l.id]: r.code === 'settlement_posted' && r.entry
      ? { text: t('aw_epay_settle_posted_why', { docNo: r.entry.docNo }), entryDocNo: r.entry.docNo }
      : { text: t(`aw_epay_err_${r.code || 'generic'}`) } }));
  });
  const delLine = (l: AwBankLine) => { if (window.confirm(t('aw_bank_line_delete_confirm'))) act(l.id, () => api.awBankLineDelete(ctx.clientId, l.id)); };

  const accountChips: OChip[] = [
    { key: '', label: t('aw_bank_all_accounts'), active: accountId === '', onClick: () => { setAccountId(''); setPage(1); } },
    // The unmatched count rides in the label — OverflowChips folds labels, not badges, so a folded
    // account still says how much work it holds.
    ...(accounts || []).map(a => ({
      key: a.id, label: a.unmatched ? `${acctLabel(a)} · ${a.unmatched}` : acctLabel(a), title: a.account_name || undefined,
      active: a.id === accountId, onClick: () => { setAccountId(a.id); setPage(1); },
    })),
  ];
  const stateChips: OChip[] = STATES.map(s => ({ key: s, label: t(`aw_bank_state_${s}`), active: s === state, onClick: () => { setState(s); setPage(1); } }));

  const rows = data?.rows || [];
  const sum = accountId ? data?.summary : undefined;
  const noAccounts = accounts !== null && !accounts.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* row 1: account chips · state chips — one row each half, folding into More when short of room */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <OverflowChips chips={accountChips} chipStyle={chip} activeStyle={chipOn} style={{ flex: '3 1 0' }} />
        <div style={{ width: 1, height: 22, background: 'var(--border-strong)', flexShrink: 0 }} aria-hidden />
        <OverflowChips chips={stateChips} chipStyle={chip} activeStyle={chipOn} style={{ flex: '2 1 0' }} />
      </div>
      {/* row 2: date range · search · actions (the search box sits on the second row, the lists' house rule) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_bank_from')} style={inp} />
        <span style={{ color: 'var(--text-tertiary)' }}>–</span>
        <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_bank_to')} style={inp} />
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); setPage(1); }} title={t('aw_bank_clear_dates')} style={{ ...actBtn, padding: '4px 7px' }}><i className="ti ti-x" aria-hidden /></button>
        )}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_bank_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
        <div style={{ flex: 1 }} />
        {/* ⛔ The export sends the filters this list is showing — account, dates, state and search —
            so the file answers the question on the screen. It is not paged: a page of 100 is a
            reading convenience, and a spreadsheet that stopped at row 100 would be reconciled
            against as though it were complete. */}
        <DlBtn style={btn} title={t('aw_export_title')}
          onRun={() => api.awBankLinesExport(ctx.clientId, { accountId, from, to, state: state === 'all' ? '' : state, q: qd })}>
          <i className="ti ti-file-spreadsheet" aria-hidden /> {t('aw_export')}
        </DlBtn>
        {!ro && (
          <button onClick={() => setDlg({ kind: 'upload' })} disabled={noAccounts} title={noAccounts ? t('aw_bank_no_accounts_line') : undefined} style={{ ...btnPrimary, opacity: noAccounts ? 0.5 : 1 }}>
            <i className="ti ti-upload" aria-hidden /> {t('aw_bank_upload')}
          </button>
        )}
        <button onClick={() => setShowStatements(v => !v)} aria-pressed={showStatements} style={{ ...btn, ...(showStatements ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>
          <i className="ti ti-files" aria-hidden /> {t('aw_bank_statements')}
        </button>
      </div>

      {err && <div style={errBox}>{err}</div>}
      {showStatements && <StatementsList ctx={ctx} accountId={accountId} onChanged={refresh} />}

      {/* One account = one currency, so its totals may be summed; across accounts the server sends none and none is made up here. */}
      {sum && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Stat label={t('aw_bank_sum_credits')} value={amt(sum.credits)} />
          <Stat label={t('aw_bank_sum_debits')} value={amt(sum.debits)} />
          <Stat label={t('aw_bank_sum_unmatched_credits')} value={amt(sum.unmatchedCredits)} tone={Number(sum.unmatchedCredits) ? '#92400E' : undefined} />
          <Stat label={t('aw_bank_sum_unmatched_debits')} value={amt(sum.unmatchedDebits)} tone={Number(sum.unmatchedDebits) ? '#92400E' : undefined} />
          <Stat label={t('aw_bank_sum_count')} value={sum.count} />
        </div>
      )}

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null || accounts === null ? <Loader /> : !rows.length ? (
          <div style={emptyLine}>{noAccounts ? t('aw_bank_no_accounts_line') : t('aw_bank_no_lines')}</div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_col_date')}</th>
                {!accountId && <th style={th}>{t('aw_bank_account')}</th>}
                <th style={th}>{t('aw_bank_description')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_debit')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_credit')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_balance')}</th>
                <th style={th}>{t('aw_bank_match')}</th>
                {!ro && <th style={th}>{t('aw_col_actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(l => {
                const opening = isOpening(l);
                const held = !!l.payroll?.holders?.length;
                const tone = opening ? 'var(--text-tertiary)' : undefined;
                return (
                  <tr key={l.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: tone }}>
                      {dmy(l.txn_date)}
                      {l.txn_time && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{l.txn_time}</div>}
                    </td>
                    {!accountId && <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: tone }}>{l.account_number}</td>}
                    <td style={{ ...td, color: tone }}>
                      <div style={{ fontWeight: opening ? 400 : 600 }}>{l.description}</div>
                      {l.details && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{l.details}</div>}
                      {(l.channel || l.source === 'manual') && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                          {l.channel && <span style={pillGrey}>{l.channel}</span>}
                          {l.source === 'manual' && <span style={pillGrey} title={l.created_by_name || undefined}>{t('aw_bank_manual')}</span>}
                        </div>
                      )}
                    </td>
                    <td style={{ ...num, color: tone }}>{Number(l.debit) ? amt(l.debit) : ''}</td>
                    <td style={{ ...num, color: tone }}>{Number(l.credit) ? amt(l.credit) : ''}</td>
                    <td style={{ ...num, color: tone }}>{l.balance === null || l.balance === undefined ? '' : amt(l.balance)}</td>
                    <td style={td}><MatchPill ctx={ctx} l={l} />
                      {/* A line's supporting files and what it is linked to (owner 2026-11-02): a paperclip and a window from the card's own file, for a read-only seat too. A statement line has no document window (sourceDoc.tsx). */}
                      {!opening && <AttachmentsBtn clientId={ctx.clientId} kind="bank_line" id={l.id} docNo={`${dmy(l.txn_date)} ${l.description}`} count={clips.counts[l.id]} onClosed={clips.refresh} />}
                      {/* The records this line's payment pays (2026-11-02, Automatic Expense Number), which the pill names only by its PMT. */}
                      {!!l.expense_payment?.expenseNos?.length && <div data-epay-line-exp-nos={l.id} title={t('aw_er_col_no')} style={{ marginTop: 3, fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', maxWidth: 220 }}>{l.expense_payment.expenseNos.join(' · ')}</div>}
                    </td>
                    {!ro && <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {/* ⛔ A line payroll holds has NO action here (batch 10 R8, R8-7): the server refuses its
                          Unmatch (409 payroll_held, which pages the admins), and Match / Ignore / Delete would
                          break the one line = one payment rule. It is freed by reversing the payment or
                          remittance in Payroll, and the sentence says so. */}
                      {!opening && held && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', display: 'inline-block', maxWidth: 260 }} data-bank-payroll-held>
                          {t('aw_bank_payroll_held')}
                        </span>
                      )}
                      {!opening && !held && l.match_state === 'none' && (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button onClick={() => setDlg({ kind: 'match', line: l })} disabled={!!busy} style={actBtn}><i className="ti ti-link" aria-hidden /> {t('aw_bank_match_btn')}</button>
                          <button onClick={() => setDlg({ kind: 'ignore', line: l })} disabled={!!busy} style={actBtn}><i className="ti ti-eye-off" aria-hidden /> {t('aw_bank_ignore_btn')}</button>
                          {l.source === 'manual' && !l.settlement?.inBooks && <button onClick={() => delLine(l)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className={busy === l.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}</button>}
                          {/* Batch 11 R5 (design §3.2.7): one payment over several bills, from this money-out line. */}
                          {Number(l.debit) > 0 && <button onClick={() => setDlg({ kind: 'paySeveral', line: l })} disabled={!!busy} style={actBtn} data-epay-pay-several={l.id}><i className="ti ti-cash" aria-hidden /> {t('aw_epay_pay_several')}</button>}
                        </span>
                      )}
                      {/* (W2 ship) A typed line an entry still in the books holds (a settlement, or a manual entry made for it
                          by hand — an unmatched line still stands) is not deleted (409 at the door): the sentence instead. */}
                      {!opening && !held && l.match_state === 'none' && l.source === 'manual' && l.settlement?.inBooks && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', maxWidth: 280, marginTop: 4 }} data-ll-delete-held={l.id}>
                          {l.settlement.reversalDraft
                            ? t('aw_ll_bank_delete_rv', { docNo: l.settlement.docNo, rv: l.settlement.reversalDraft })
                            : l.settlement.closed
                              ? t('aw_ll_bank_delete_closed', { docNo: l.settlement.docNo, from: dmy(l.settlement.closed.start), to: dmy(l.settlement.closed.end) })
                              : t('aw_ll_bank_delete_books', { docNo: l.settlement.docNo })}
                        </div>
                      )}
                      {/* ⛔ Batch 11 R3 (design §3.5.4 rows 10–11): a line whose settlement is still in the books is not
                          freed — the server refuses its Unmatch (409 settlement_in_books, which pages the admins). The
                          sentence names the entry, and "Reverse JV-…" opens the line's entries panel on it. */}
                      {!opening && !held && l.match_state !== 'none' && l.settlement?.inBooks && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', display: 'inline-flex', flexDirection: 'column', gap: 4, maxWidth: 280 }} data-ll-settled={l.settlement.docNo}>
                          <span>{l.settlement.reversalDraft
                            ? t('aw_ll_bank_settled_draft', { docNo: l.settlement.docNo, rv: l.settlement.reversalDraft })
                            : l.settlement.closed
                              ? t('aw_ll_bank_settled_closed', { docNo: l.settlement.docNo, from: dmy(l.settlement.closed.start), to: dmy(l.settlement.closed.end) })
                              : t(l.match_state === 'transfer' ? 'aw_ll_transfer_settled' : 'aw_ll_bank_settled', { docNo: l.settlement.docNo })}</span>
                          {/* (W2 ship, S4) An entry posted in a closed period is not reversed: no "Reverse" to press. */}
                          {!l.settlement.closed && <ReverseEntryBtn ctx={ctx} kind="bank_line" id={l.settlement.lineId || l.id} docNo={l.description} entry={l.settlement} style={{ ...actBtn, padding: '3px 8px', fontSize: 11.5 }} />}
                        </span>
                      )}
                      {/* A payment line is never a bare Unmatch (batch 11 R5, W2-18): a batch 11 payment is reversed with its
                          reason ("Reverse PMT-…"), an older one undone with its reason; every other mark unmatches as before —
                          each only while the line's settlement is not in the books (R3's sentence above stands instead). */}
                      {!opening && !held && l.match_state !== 'none' && !l.settlement?.inBooks && (l.topup ? (
                        /* (Batch 11 W4 R7, P7b task 4) A top-up is undone with its reason ("Undo top-up…", TopUpUndoDialog) — never
                           a bare Unmatch (400 reason_required); dated in a closed period it is not undone at all (409 period_closed),
                           and the sentence says so instead. Its settlement in the books: R3's sentence above stands instead. */
                        l.topup.closed ? (
                          <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', display: 'inline-block', maxWidth: 280 }} data-topup-closed={l.id}>
                            {t('aw_topup_closed', { from: dmy(l.topup.closed.start), to: dmy(l.topup.closed.end) })}
                          </span>
                        ) : (
                          <button onClick={() => setDlg({ kind: 'undoTopup', line: l })} disabled={!!busy} style={actBtn} data-topup-undo={l.id}>
                            <i className="ti ti-arrow-back-up" aria-hidden /> {t('aw_topup_undo_btn')}
                          </button>
                        )
                      ) : l.expense_payment ? (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          <button onClick={() => reverseFor(l)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} data-epay-reverse-line={l.id}>
                            <i className={busy === l.id ? 'ti ti-loader' : 'ti ti-arrow-back-up'} aria-hidden /> {t('aw_epay_reverse_btn', { payNo: l.expense_payment.payNo })}
                          </button>
                          {!!lineWhy[l.id] && (
                            <span style={{ fontSize: 11.5, color: '#92400E', whiteSpace: 'normal', maxWidth: 260 }} data-epay-line-why={l.id}>
                              {lineWhy[l.id].text}{lineWhy[l.id].entryDocNo && <> <DocEntriesBtn ctx={ctx} kind="bank_line" id={l.id} docNo={lineWhy[l.id].entryDocNo || ''} /></>}
                            </span>
                          )}
                        </span>
                      ) : l.match_state === 'matched' && l.match_kind === 'payment' ? (
                        <button onClick={() => setDlg({ kind: 'undoOld', line: l })} disabled={!!busy} style={actBtn} data-epay-undo-old={l.id}>
                          <i className="ti ti-link-off" aria-hidden /> {t('aw_epay_undo_old')}
                        </button>
                      ) : l.prepaidHold ? (
                        /* (W3.1) A receipt's line whose Unmatch would leave the customer's withholding for no receipt (a hand
                           draft carries it) or nowhere (a posted settlement of the receipt left it to this line): the server
                           refuses it (409 wht_prepaid_held, which pages the admins) — the sentence instead, the server's as hover. */
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', display: 'inline-block', maxWidth: 280 }}
                          data-bank-prepaid-held={l.prepaidHold.case} title={l.prepaidHold.message}>
                          {prepaidHoldText(t, l.prepaidHold)}
                        </span>
                      ) : (
                        <button onClick={() => unmatch(l)} disabled={!!busy} style={actBtn} title={l.match_state === 'transfer' ? t('aw_bank_unmatch_transfer_hint') : undefined}>
                          <i className={busy === l.id ? 'ti ti-loader' : 'ti ti-link-off'} aria-hidden /> {t('aw_bank_unmatch_btn')}
                        </button>
                      ))}
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Pager driven by the SERVER's echoed limit, the workspace convention (Documents.tsx). */}
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_bank_marks_only_note')}</div>

      {!ro && accounts && accounts.length > 0 && <Fab onClick={() => setDlg({ kind: 'manual' })} title={t('aw_bank_add_line')} />}
      {!ro && dlg?.kind === 'upload' && <UploadDialog ctx={ctx} accounts={accounts || []} defaultAccountId={accountId} onClose={() => setDlg(null)} onUploaded={refresh} />}
      {!ro && dlg?.kind === 'manual' && <ManualLineDialog ctx={ctx} accounts={accounts || []} defaultAccountId={accountId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {!ro && dlg?.kind === 'match' && <MatchDialog ctx={ctx} line={dlg.line} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {!ro && dlg?.kind === 'ignore' && <IgnoreDialog ctx={ctx} line={dlg.line} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {!ro && dlg?.kind === 'paySeveral' && <PaymentDialog ctx={ctx} bills={[]} line={dlg.line} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {!ro && dlg?.kind === 'reverse' && (
        <ReasonDialog title={t('aw_epay_reverse_title', { payNo: dlg.line.expense_payment?.payNo || '' })} docNo={dlg.line.expense_payment?.payNo || undefined}
          line={<>
            <b style={{ color: 'var(--text-primary)' }}>{dlg.line.expense_payment?.payNo}</b> · {dmy(dlg.line.txn_date)} · {dlg.line.description} · {amt(dlg.line.debit)}
            {dlg.r.whtEffects.map(e => <div key={e.whtLineId} style={{ marginTop: 4 }}>{t(`aw_epay_reverse_wht_${e.action}`)}{e.certNo ? ` ${t('aw_epay_reverse_cert', { cert: e.certNo })}` : ''}</div>)}
            {!!dlg.r.draftsToDiscard.length && <div style={{ marginTop: 4 }}>{t('aw_epay_reverse_drafts', { docNos: dlg.r.draftsToDiscard.join(', ') })}</div>}
          </>}
          hint={t('aw_epay_reverse_hint')} icon="ti ti-arrow-back-up" label={t('aw_epay_reverse_btn', { payNo: dlg.line.expense_payment?.payNo || '' })}
          onClose={() => setDlg(null)}
          onSubmit={async reason => { await epayApi.reverse(ctx.clientId, String(dlg.line.expense_payment?.id), reason); setDlg(null); refresh(); }} />
      )}
      {!ro && dlg?.kind === 'undoTopup' && <TopUpUndoDialog ctx={ctx} line={dlg.line} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {!ro && dlg?.kind === 'undoOld' && (
        <ReasonDialog title={t('aw_epay_old_delete_title')} docNo={dlg.line.match_doc_no || undefined} line={<><b style={{ color: 'var(--text-primary)' }}>{dlg.line.match_doc_no}</b> · {dmy(dlg.line.txn_date)} · {dlg.line.description} · {amt(dlg.line.debit)}</>}
          hint={t('aw_epay_old_delete_hint')} icon="ti ti-link-off" label={t('aw_epay_undo_old')} onClose={() => setDlg(null)}
          onSubmit={async reason => { await api.awBankUnmatch(ctx.clientId, dlg.line.id, reason); setDlg(null); refresh(); }} />
      )}
    </div>
  );
}

// ── Reconciliation pane ─────────────────────────────────────────────────────────────────────────
// One account at a time, in its own currency. Counts and totals are the account's statement lines
// by match state; the latest statement's closing balance is printed as the bank stated it. No ledger
// comparison here — that would be a new figure, and this batch invents none.
export function ReconciliationPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState<AwBankAcct[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState<AwBankReconciliation | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankAccts(ctx.clientId).then((d: AwBankAccts) => {
      if (!alive) return;
      const rows: AwBankAcct[] = d.rows || [];
      setAccounts(rows);
      setAccountId(cur => (cur && rows.some(a => a.id === cur)) ? cur : (rows[0]?.id || ''));
    }).catch((e: any) => { if (alive) { setAccounts([]); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 当月核对一致 — which month is being reconciled, and against which ledger account. The month
  // defaults to the newest one the statements cover, because that is the one being worked on.
  const [ym, setYm] = useState('');
  const [coa, setCoa] = useState('');
  const [signing, setSigning] = useState(false);
  // A read-only seat picks the month and the ledger account (both are reads) and reads the check and who
  // signed it — but signs nothing and runs no settlement, not even the preview: a dry run is still a POST,
  // and the server refuses a read-only seat by METHOD (middleware.ts).
  const ro = isReadOnlySeat(ctx);
  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    setData(null);
    api.awBankReconciliation(ctx.clientId, accountId, { ym, coaAccountId: coa })
      .then((d: any) => {
        if (!alive) return;
        setData(d); setErr('');
        if (!ym && d.months?.[0]?.ym) setYm(String(d.months[0].ym));
        if (!coa && d.recon?.coaAccountId) setCoa(String(d.recon.coaAccountId));
        // (Batch 11 W3, W3-16) A pairing locked by settlements in the books is the only one the picker shows.
        const lockedTo = d.recon?.pairingLock?.inBooks?.length ? String(d.recon.pairingLock.pairedAccountId || '') : '';
        if (lockedTo && lockedTo !== coa) setCoa(lockedTo);
      })
      .catch((e: any) => { if (alive) setErr(readErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, accountId, ym, coa]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Receipts and payments into the ledger (owner 2026-10-16) ──────────────────────────────
  // The difference above is not an error in the statement: it is the part of the bank movement the
  // books have not been told about. This is the button that tells them — and it previews first,
  // because an operator should see which lines will post and which are left for them BEFORE
  // anything is in the books.
  // (Batch 11 W3 R6, P6c — the W3 brief §3 P6c tasks 1 and 5; design §3.3.3) The report is the server's, worded by CODE
  // (settleSkipText) with each row's way on, its skipped lines 100 a page (one request per page: the preview asked again
  // for that page); a dry run's entries carry their legs, shown on request; a closed period's preview says so and leaves
  // Generate off. A Confirm in the voucher dialog re-runs the preview by itself.
  const [settle, setSettle] = useState<SettleReport | null>(null);
  const [settling, setSettling] = useState('');
  const [showLegs, setShowLegs] = useState(false);
  const [acking, setAcking] = useState<SettleSkip | null>(null);
  const runSettle = async (commit: boolean, page = 1) => {
    setSettling(commit ? 'commit' : 'preview'); setErr('');
    try {
      const r = await settleApi.run(ctx.clientId, { periodId: ctx.periodId, dryRun: !commit, skippedPage: page });
      setSettle(r);
      if (commit) ctx.bump();   // ctx.reload re-runs this pane's own effects, balances included
    } catch (e: any) { setErr(readErr(e, t)); } finally { setSettling(''); }
  };

  // ⛔ (Batch 11 W3, W3-16: pairing_in_use) A pairing whose settlements are in the books is LOCKED: the picker is disabled on
  // it and says which entries hold it ("Reverse JV-…" for a seat that writes the books), so the sign-off never sends
  // another account the server would refuse. With only drafts the pairing may change: choosing another account says
  // which drafts the sign-off discards, and after it the discarded ones are named.
  const lock = data?.recon?.pairingLock || null;
  const lockedTo = lock && lock.inBooks.length ? lock.pairedAccountId : '';
  const coaUsed = lockedTo || coa;
  const codeOf = (id: string) => (data?.recon?.coaOptions || []).find(a => a.id === id)?.code || '';
  const draftsAtRisk = !!lock && !lockedTo && !!lock.drafts.length && !!coa && coa !== lock.pairedAccountId;
  // (Owner decision 2026-10-27: 「以后可以改」) Settlements posted in a CLOSED period no longer lock the picker: they stay on
  // the account they were posted to. Choosing another account says so first, in one sentence — the old account keeps their
  // balance; a manual transfer voucher in the current period moves it if it should (the server re-pairs and records it).
  // (The W4 gate review's low) Only the entries that stay BEHIND: none on the account chosen (back to where they sit says nothing).
  const staying = lock && coa ? (lock.closedStay || []).filter(e => !((lock.closedStayAt || {})[e.entryId] || []).includes(coa)) : [];
  const stayFrom = lock ? (Array.from(new Set(staying.flatMap(e => (lock.closedStayAt || {})[e.entryId] || []).map(id => codeOf(id)).filter(Boolean))).sort().join(', ')
    || (lock.closedStayOn || []).join(', ') || codeOf(lock.pairedAccountId)) : '';
  const staysAtRisk = !!lock && !lockedTo && !!staying.length && !!coa && coa !== lock.pairedAccountId;
  // (W4-17; the W4 gate review) The lock's entries by their way out: settlements (Reverse here), payroll entries (Payroll ›
  // Payroll List — never a Reverse from Banking) and payroll entries posted in a closed period (they stay; Payroll refuses
  // them too, so the pairing cannot change while they do).
  const lockSettle = lock ? lock.inBooks.filter(e => e.kind !== 'payroll' && !closedOf([e]).length) : [];
  const lockPay = lock ? lock.inBooks.filter(e => e.kind === 'payroll' && !e.closed) : [];
  const lockPayShut = lock ? lock.inBooks.filter(e => e.kind === 'payroll' && !!e.closed) : [];
  const lockNos = (es: { entryId: string; docNo: string }[]) => es.map(e => e.docNo || e.entryId.slice(0, 8)).join(', ');
  const openPayroll = (e: { runId?: string; remitId?: string }) => (e.runId
    ? ctx.setNav('payroll', 'payroll_list', { run: e.runId, prview: '', remit: '' })
    : e.remitId ? ctx.setNav('payroll', 'payroll_list', { prview: 'remittances', remit: e.remitId, run: '' })
      : ctx.setNav('payroll', 'payroll_list', { run: '', prview: '', remit: '' }));
  const [discarded, setDiscarded] = useState<string[]>([]);
  // What the sign-off answered when it re-paired past settlements posted in a closed period (`repaired`): the same sentence,
  // now as done — they stayed on the old account.
  const [repaired, setRepaired] = useState<{ from: string; to: string; stays: string[]; stayOn?: string[] } | null>(null);
  const signOff = async () => {
    if (!accountId || !ym || !coaUsed) return;
    setSigning(true); setErr(''); setDiscarded([]); setRepaired(null);
    try {
      const r: any = await api.awBankReconSign(ctx.clientId, accountId, ym, coaUsed);
      setData((d: any) => d ? { ...d, recon: r.recon } : d);
      if (Array.isArray(r?.discarded) && r.discarded.length) setDiscarded(r.discarded.map(String));
      if (r?.repaired && Array.isArray(r.repaired.stays) && r.repaired.stays.length) setRepaired(r.repaired);
    } catch (e: any) { setErr(readErr(e, t)); } finally { setSigning(false); }
  };
  // Same page, Transactions submenu, pre-filtered: the pane reads ?state=&accountId= on mount.
  // It writes ?menu=&sub= itself (it must carry ?state=&accountId= too, which ctx.setNav does not),
  // so it re-keys the pane like any menu chip — and asks first like one (utils/unsaved.ts; break-it
  // review 2026-10-20: it bypassed ctx.setNav, so it was a door the guard never saw).
  const workUnmatched = () => guardLeave(() => setParams(p => {
    const n = new URLSearchParams(p); n.set('menu', 'banking'); n.set('sub', 'transactions'); n.set('state', 'unmatched'); n.set('accountId', accountId); return n;
  }, { replace: true }));

  if (accounts === null) return <Loader />;
  if (!accounts.length) return <div style={emptyLine}>{t('aw_bank_no_accounts_line')}</div>;
  const acct = data?.account || accounts.find(a => a.id === accountId) || null;
  const cur = acct?.currency ? ` (${acct.currency})` : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('aw_bank_account')}</label>
        {/* (W3 ship review, low) Another bank account starts from ITS pairing: the ledger account chosen for the last one
            (and what its sign-off discarded) is not carried over, so B is never signed with A's cash account. */}
        <select value={accountId} onChange={e => { setAccountId(e.target.value); setCoa(''); setDiscarded([]); setRepaired(null); }} style={{ ...inp, minWidth: 220 }}>
          {accounts.map(a => <option key={a.id} value={a.id}>{acctLabel(a)}{a.currency ? ` · ${a.currency}` : ''}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={workUnmatched} disabled={!data || !data.counts.unmatched} style={{ ...btnPrimary, opacity: !data || !data.counts.unmatched ? 0.5 : 1 }}>
          <i className="ti ti-list-check" aria-hidden /> {t('aw_bank_rc_work')}
        </button>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {!data ? <Loader /> : (
        <>
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_counts')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Stat label={t('aw_bank_rc_lines')} value={data.counts.lines} />
              <Stat label={t('aw_bank_state_matched')} value={data.counts.matched} tone={data.counts.matched ? '#166534' : undefined} />
              <Stat label={t('aw_bank_state_transfer')} value={data.counts.transfer} tone={data.counts.transfer ? '#1E40AF' : undefined} />
              <Stat label={t('aw_bank_state_ignored')} value={data.counts.ignored} />
              <Stat label={t('aw_bank_state_unmatched')} value={data.counts.unmatched} tone={data.counts.unmatched ? '#92400E' : undefined} />
            </div>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_amounts')}{cur}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Stat label={t('aw_bank_sum_credits')} value={amt(data.amounts.credits)} />
              <Stat label={t('aw_bank_sum_debits')} value={amt(data.amounts.debits)} />
              <Stat label={t('aw_bank_sum_unmatched_credits')} value={amt(data.amounts.unmatchedCredits)} tone={Number(data.amounts.unmatchedCredits) ? '#92400E' : undefined} />
              <Stat label={t('aw_bank_sum_unmatched_debits')} value={amt(data.amounts.unmatchedDebits)} tone={Number(data.amounts.unmatchedDebits) ? '#92400E' : undefined} />
              {data.latest_statement
                ? <Stat label={t('aw_bank_rc_closing')} value={data.latest_statement.closing_balance === null || data.latest_statement.closing_balance === undefined ? '—' : amt(data.latest_statement.closing_balance)} sub={data.latest_statement.period_to ? `${t('aw_bank_rc_as_of')} ${dmy(data.latest_statement.period_to)}` : undefined} />
                : <Stat label={t('aw_bank_rc_closing')} value="—" sub={t('aw_bank_rc_no_statement')} />}
            </div>
          </div>
          {/* ── 当月核对一致 ─────────────────────────────────────────────────────────────────
              The statement's closing balance for the month, the book balance of the 银行存款
              account, and the difference between them — then a signature saying somebody checked.
              ⛔ The note under it is not boilerplate: nothing in this system posts to the bank
              account in the ledger except a hand-typed voucher, so until the client's receipts and
              payments are entered the difference IS the book side, and a reader has to know that
              rather than read a red number as an error in the data. */}
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_month_check')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
              <select value={ym} onChange={e => setYm(e.target.value)} style={{ ...inp, width: 130 }}>
                {(data.months || []).map((m: any) => <option key={m.ym} value={m.ym}>{m.ym}</option>)}
              </select>
              <select value={coaUsed} onChange={e => { setCoa(e.target.value); setDiscarded([]); setRepaired(null); }} disabled={!!lockedTo} data-recon-coa={lockedTo ? 'locked' : 'free'}
                style={{ ...inp, minWidth: 250, opacity: lockedTo ? 0.7 : 1 }}>
                <option value="">{t('aw_bank_rc_pick_account')}</option>
                {(data.recon?.coaOptions || []).map((a: any) => <option key={a.id} value={a.id}>{a.code} · {a.name_th}</option>)}
              </select>
              <div style={{ flex: 1 }} />
              {!ro && (
                <button onClick={signOff} disabled={signing || !ym || !coaUsed || !data.recon || data.recon.statementBalance === null}
                  style={{ ...btn, opacity: (signing || !ym || !coaUsed) ? 0.5 : 1 }} data-recon-sign>
                  <i className={signing ? 'ti ti-loader' : 'ti ti-writing-sign'} aria-hidden /> {t('aw_bank_rc_sign')}
                </button>
              )}
            </div>
            {!!lockedTo && lock && (
              <div data-pair-locked={lockedTo}
                style={{ fontSize: 12, color: '#92400E', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '7px 11px', marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* (W3.1, the state lens' low) A settlement whose reversal is raised already is not reversed again: "post R…".
                    (Owner decision 2026-10-27) A settlement posted in a closed period no longer locks: only open-period ones are here.
                    (W4-17; the W4 gate review) A payroll entry is reversed in Payroll, never from Banking: its own sentence and a jump
                    to Payroll › Payroll List; one posted in a closed period stays (Payroll refuses it too) — said, nothing offered. */}
                {lockPayShut.length ? (
                  <span data-pair-locked-payroll="closed"><i className="ti ti-lock" aria-hidden /> {t('aw_pair_locked_payroll_closed', { code: codeOf(lockedTo) || '—', docNos: lockNos(lockPayShut),
                    from: dmy(lockPayShut[0].closed?.start || ''), to: dmy(lockPayShut[0].closed?.end || '') })}</span>
                ) : (<>
                  {!!lockSettle.length && <span><i className="ti ti-lock" aria-hidden /> {pairLockText(t, codeOf(lockedTo) || '—', lockSettle)}</span>}
                  {lockSettle.slice(0, 3).map(e => (
                    <ReverseEntryBtn key={e.entryId} ctx={{ clientId: ctx.clientId }} kind="bank_line" id={e.lineId} docNo={e.docNo} entry={{ entryId: e.entryId, docNo: e.docNo, reversalDraft: e.reversalDraft || null }} />
                  ))}
                  {!!lockPay.length && <span data-pair-locked-payroll="open"><i className="ti ti-lock" aria-hidden /> {t('aw_pair_locked_payroll', { code: codeOf(lockedTo) || '—', docNos: lockNos(lockPay) })}</span>}
                  {!!lockPay.length && !ro && (
                    <button type="button" onClick={() => openPayroll(lockPay[0])} style={{ ...btn, padding: '3px 8px', fontSize: 11.5 }} data-pair-open-payroll={lockPay[0].runId || lockPay[0].remitId || ''}>
                      <i className="ti ti-users" aria-hidden /> {t('aw_pair_open_payroll')}
                    </button>
                  )}
                </>)}
              </div>
            )}
            {!ro && staysAtRisk && lock && (
              <div data-pair-stays={staying.map(e => e.docNo || e.entryId.slice(0, 8)).join(',')} style={{ fontSize: 12, color: '#92400E', marginBottom: 8 }}>
                <i className="ti ti-info-circle" aria-hidden /> {t('aw_pair_closed_stays', { docNos: lockNos(staying), from: stayFrom || '—', to: codeOf(coa) || '—' })}
              </div>
            )}
            {!ro && draftsAtRisk && lock && (
              <div data-pair-drafts style={{ fontSize: 12, color: '#92400E', marginBottom: 8 }}>
                <i className="ti ti-alert-triangle" aria-hidden /> {t('aw_pair_drafts_warning', { code: codeOf(coa) || '—', docNos: lock.drafts.map(d => d.docNo || d.entryId.slice(0, 8)).join(', ') })}
              </div>
            )}
            {!!repaired && (
              <div data-pair-repaired={repaired.stays.join(',')} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                <i className="ti ti-info-circle" aria-hidden /> {t('aw_pair_closed_stays', { docNos: repaired.stays.join(', '), from: (repaired.stayOn || []).join(', ') || repaired.from || '—', to: repaired.to || '—' })}
              </div>
            )}
            {!!discarded.length && (
              <div data-pair-discarded style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('aw_pair_discarded', { docNos: discarded.join(', ') })}</div>
            )}
            {!(data.recon?.coaOptions || []).length && (
              <div style={{ fontSize: 11.5, color: '#B45309', marginBottom: 8 }}>{t('aw_bank_rc_no_cash_account')}</div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Stat label={t('aw_bank_rc_stmt_balance')} value={data.recon?.statementBalance === null || data.recon?.statementBalance === undefined ? '—' : amt(data.recon.statementBalance)}
                sub={data.recon?.monthEnd ? `${t('aw_bank_rc_as_of')} ${dmy(data.recon.monthEnd)}` : undefined} />
              <Stat label={t('aw_bank_rc_book_balance')} value={data.recon?.bookBalance === null || data.recon?.bookBalance === undefined ? '—' : amt(data.recon.bookBalance)}
                sub={!coa ? t('aw_bank_rc_pick_account') : undefined} />
              <Stat label={t('aw_bank_rc_difference')} value={data.recon?.difference === null || data.recon?.difference === undefined ? '—' : amt(data.recon.difference)}
                tone={Number(data.recon?.difference) ? '#B42318' : '#166534'}
                sub={data.recon?.unexplained?.lines || data.recon?.unexplained?.byHand?.lines
                  ? `${t('aw_bank_rc_unexplained', { n: data.recon.unexplained.lines })}${data.recon.unexplained.byHand?.lines ? ` ${t('aw_bank_rc_unexplained_by_hand', { m: data.recon.unexplained.byHand.lines })}` : ''}`
                  : undefined} />
            </div>
            {data.recon?.signed && (
              <div style={{ fontSize: 12, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', borderRadius: 8, padding: '7px 11px', marginTop: 8 }}>
                <i className="ti ti-check" aria-hidden /> {t('aw_bank_rc_signed_by', { name: data.recon.signed.by, at: dmy(new Date(data.recon.signed.at * 1000).toISOString().slice(0, 10)) })}
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{data.recon.signed.note}</div>
              </div>
            )}
            <div style={note}>{t('aw_bank_rc_book_note')}</div>
            {!ro && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
              <button onClick={() => runSettle(false)} disabled={!!settling || !ctx.periodId} style={{ ...btn, opacity: settling ? 0.5 : 1 }} data-settle-preview>
                <i className={settling === 'preview' ? 'ti ti-loader' : 'ti ti-eye'} aria-hidden /> {t('aw_settle_preview')}
              </button>
              <button onClick={() => runSettle(true)} disabled={!!settling || !settle || !settle.created || !!settle.periodRefusal} style={{ ...btnPrimary, opacity: (settling || !settle?.created || settle?.periodRefusal) ? 0.5 : 1 }} data-settle-run>
                <i className={settling === 'commit' ? 'ti ti-loader' : 'ti ti-arrow-big-right-lines'} aria-hidden /> {t('aw_settle_run')}
              </button>
              {settle && !settle.periodRefusal && (
                <span style={{ fontSize: 12.5, color: settle.dryRun ? 'var(--text-secondary)' : '#166534' }} data-settle-summary>
                  {t(settle.dryRun ? 'aw_settle_would' : 'aw_settle_done', { n: settle.created, amount: amt(settle.amount) })}
                  {!!(settle.skippedTotal || settle.skipped?.length) && ` · ${t('aw_settle_left', { n: settle.skippedTotal || settle.skipped.length })}`}
                </span>
              )}
              {/* (W1 ship) Matched lines a manual entry made for them by hand holds — not settled again, and said. */}
              {!!settle?.byHand?.length && (
                <span data-settle-by-hand style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {t('aw_settle_by_hand', { n: settle.byHand.length,
                    list: settle.byHand.slice(0, 4).map((x: any) => `${dmy(x.date)} ${settleSourceLabel(t, x)} → ${x.entryNo}`).join('; ') + (settle.byHand.length > 4 ? ' …' : '') })}
                </span>
              )}
            </div>}
            {/* (Batch 11 W3, W3-12) A closed period's preview is a 200 that says so — never a refusal, and nothing to generate. */}
            {!ro && !!settle?.periodRefusal && (
              <div data-settle-period-closed style={{ ...errBox, marginTop: 8 }}><i className="ti ti-lock" aria-hidden /> {t('aw_settle_period_closed')}</div>
            )}
            {/* (Batch 11 W3, W3-26) What each entry would post — its legs, with the account each one names and the party of
                the document's VAT line — before anything is written. */}
            {!ro && !!settle?.dryRun && !!settle.entries?.some(e => (e.legs || []).length) && (
              <div style={{ marginTop: 8 }}>
                <button type="button" onClick={() => setShowLegs(v => !v)} style={{ ...actBtn, padding: '2px 9px' }} data-settle-legs-toggle>
                  <i className={showLegs ? 'ti ti-chevron-up' : 'ti ti-chevron-down'} aria-hidden /> {t(showLegs ? 'aw_settle_legs_hide' : 'aw_settle_legs')}
                </button>
                {showLegs && (
                  <div style={{ ...card, padding: 0, overflowX: 'auto', marginTop: 6 }}>
                    <table style={table} data-settle-legs>
                      <thead><tr>
                        <th style={th}>{t('aw_bank_match_doc')}</th><th style={th} />
                        <th style={th}>{t('aw_settle_col_account')}</th><th style={th}>{t('aw_settle_col_party')}</th>
                        <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                      </tr></thead>
                      <tbody>
                        {settle.entries.map(e => (e.legs || []).map((g, i) => (
                          <tr key={`${e.lineId}:${i}`} data-settle-leg={e.lineId} data-leg-role={g.role} data-leg-side={g.dr ? 'dr' : 'cr'}>
                            <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: i ? 400 : 700, color: i ? 'var(--text-tertiary)' : undefined }}>{i ? '' : `${settleSourceLabel(t, e)} · ${amt(e.amount)}`}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap', paddingLeft: g.dr ? 8 : 28 }}>{t(g.dr ? 'aw_gl_dr_short' : 'aw_gl_cr_short')}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{g.accountCode} · {t(`aw_settle_leg_${g.role}`)}</td>
                            <td style={td}>{g.partnerName || '—'}</td>
                            <td style={num}>{baht(g.dr || g.cr)}</td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {!ro && !!settle?.skipped?.length && (
              <div style={{ marginTop: 8 }}>
                <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                  <table style={table} data-settle-skipped>
                    <thead><tr>
                      <th style={th}>{t('aw_col_date')}</th>
                      <th style={th}>{t('aw_bank_match_doc')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                      <th style={th}>{t('aw_settle_why')}</th>
                    </tr></thead>
                    <tbody>
                      {settle.skipped.map(x => (
                        <tr key={x.lineId} data-settle-skip={x.lineId} data-skip-code={x.code}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(x.date)}</td>
                          <td style={td} data-settle-src={x.sourceKind || 'bank_line'}>{settleSourceLabel(t, x)}</td>
                          <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{amt(x.amount)}</td>
                          <td style={{ ...td, color: '#92400E' }}>
                            <span title={x.why || undefined} data-skip-why>{settleSkipText(t, x)}</span>
                            <SkipWayOn ctx={ctx} x={x} onReview={() => setAcking(x)} onBack={() => runSettle(false, settle.skippedPage || 1)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(settle.skippedTotal || 0) > (settle.limit || 100) && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }} data-settle-page>
                    {t('aw_settle_page', { from: ((settle.skippedPage || 1) - 1) * (settle.limit || 100) + 1,
                      to: ((settle.skippedPage || 1) - 1) * (settle.limit || 100) + settle.skipped.length, total: settle.skippedTotal })}
                  </div>
                )}
                <div data-settle-pager>
                  <Pagination page={settle.skippedPage || 1} total={settle.skippedTotal || settle.skipped.length} limit={settle.limit || 100}
                    onPage={n => { if (!settling) runSettle(false, n); }} />
                </div>
              </div>
            )}
            {acking && (
              <SettleAckDialog ctx={ctx} source={{ kind: acking.sourceKind === 'cash_line' ? 'cash_line' : 'bank_line', id: acking.lineId }} line={{ date: acking.date, amount: acking.amount }} onClose={() => setAcking(null)}
                onDone={remaining => { if (!remaining) setAcking(null); runSettle(false, settle?.skippedPage || 1); }} />
            )}
          </div>
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_months')}</div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!data.months.length ? <div style={emptyLine}>{t('aw_bank_no_lines')}</div> : (
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>{t('aw_bank_rc_month')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_sum_credits')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_sum_debits')}</th>
                      {/* `matched` per month counts every EXPLAINED line — matched, transfer and ignored — while
                          the card above counts 'matched' alone, so this column is headed accordingly. */}
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_rc_explained')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_state_unmatched')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map(m => {
                      const [y, mo] = String(m.ym || '').split('-');
                      return (
                        <tr key={m.ym}>
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{y && mo ? `${mo}/${y}` : m.ym}</td>
                          <td style={num}>{amt(m.credits)}</td>
                          <td style={num}>{amt(m.debits)}</td>
                          <td style={num}>{m.matched}</td>
                          <td style={{ ...num, color: m.unmatched ? '#92400E' : 'var(--text-tertiary)', fontWeight: m.unmatched ? 700 : 400 }}>{m.unmatched}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div style={note}>{t('aw_bank_rc_note')}</div>
        </>
      )}
    </div>
  );
}

// ── Transfers pane ──────────────────────────────────────────────────────────────────────────────
// A suggestion = a debit in one of the company's accounts and a credit in another, equal amount,
// within 3 days, both still unmatched (the server's rule, applied there). Confirming marks the two
// lines 'transfer' and pairs them; Undo returns both to unmatched. Nothing is posted anywhere.
export function TransfersPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwBankTransfers | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankTransfers(ctx.clientId).then((d: AwBankTransfers) => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) { setData({ suggestions: [], confirmed: [] }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Confirm / undo re-read through ctx.bump — the workspace's one signal, as in every writing pane.
  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  // A read-only seat reads both lists and is drawn neither Confirm nor Undo (nor their column).
  const ro = isReadOnlySeat(ctx);
  // (The W4 gate review's low) What an Undo discarded — the pair's settlement draft, named as the unmatch names it.
  const [undone, setUndone] = useState<string[]>([]);
  const confirmPair = (s: AwBankTransferSuggestion) => { setUndone([]); return act(`${s.debit.id}:${s.credit.id}`, () => api.awBankTransferConfirm(ctx.clientId, s.debit.id, s.credit.id)); };
  const undo = (c: AwBankTransferConfirmed) => { setUndone([]); return act(c.pair_id, async () => {
    const r: any = await api.awBankTransferUndo(ctx.clientId, c.pair_id);
    setUndone(Array.isArray(r?.discarded) ? r.discarded.map(String) : []);
  }); };
  // (Batch 11 W4 R7, P7b task 5) Each confirmed pair's settlement, by its holder `transfer:<pair>` (its DEBIT line's — the
  // credit line is never a source): `ready` → "Create draft entry" (Generate for that one line, no period); not in the books
  // → why, in the report's words, and "Review N vouchers". (Batch 12 tail T5, COA-D2) The debit line's entries panel is on
  // EVERY confirmed pair, whatever its settle state and for every seat (entriesOf below) — no longer only on draft / posted.
  const [acking, setAcking] = useState<AwBankTransferConfirmed | null>(null);
  const createFor = (c: AwBankTransferConfirmed) => act(`gen-${c.pair_id}`, () => settleApi.run(ctx.clientId, { dryRun: false, only: { bankLineIds: [String(c.debit_line_id || '')] } }));
  const settleCell = (c: AwBankTransferConfirmed): React.ReactNode => {
    const st: EpaySettle | null | undefined = c.settle;
    if (!st) return '—';
    if (st.state === 'reversed' && st.previous) return <span data-tr-settle="reversed">{t('aw_epay_settle_reversed_by', { docNo: st.previous.docNo || '—', reversal: st.previous.reversalDocNo || '—' })}</span>;
    const head = <span data-tr-settle={st.state}>{t(`aw_epay_settle_${st.state}`, { docNo: st.entry?.docNo || '' })}</span>;
    if (st.state === 'ready') {
      return (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {head}
          {!ro && !!c.debit_line_id && (
            <button type="button" disabled={!!busy} onClick={() => createFor(c)} data-tr-create={c.pair_id} style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
              <i className={busy === `gen-${c.pair_id}` ? 'ti ti-loader' : 'ti ti-file-plus'} aria-hidden /> {t('aw_epay_create_draft')}
            </button>
          )}
        </span>
      );
    }
    if (st.state === 'not_in_books' && st.skip) {
      const sk = st.skip;
      const n = Number(sk.candidates) || Number(sk.params?.n) || 0;
      return (
        <span style={{ display: 'inline-block', whiteSpace: 'normal', maxWidth: 340 }}>
          {head}
          <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 2 }} title={sk.why || undefined} data-tr-skip={sk.code}>{settleSkipText(t, { code: sk.code, why: sk.why, params: sk.params })}</div>
          {sk.code === 'manual_settlement_candidates' && !!c.debit_line_id && (
            <button type="button" onClick={() => setAcking(c)} data-tr-review={c.pair_id} style={{ ...actBtn, marginTop: 3 }}>
              <i className="ti ti-list-check" aria-hidden /> {t('aw_epay_review_vouchers', { n })}
            </button>
          )}
        </span>
      );
    }
    return head;
  };
  /** (Batch 12 tail T5) The pair's entries — its debit line's, the transfer's one source (the credit line's panel points
   *  there) — beside the settle cell on every confirmed pair, outside the read-only seat's missing Actions column. */
  const entriesOf = (c: AwBankTransferConfirmed): React.ReactNode => (c.debit_line_id
    ? <DocEntriesBtn ctx={ctx} kind="bank_line" id={c.debit_line_id} docNo={`${dmy(c.date)} ${c.from_account_number} → ${c.to_account_number}`}
        label={<i className="ti ti-book-2" aria-hidden />} style={{ ...actBtn, padding: '1px 6px', marginLeft: 6 }} />
    : null);
  const sugg = data?.suggestions || [];
  const conf = data?.confirmed || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {err && <div style={errBox}>{err}</div>}
      {!!undone.length && <div data-tr-undo-discarded style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('aw_bank_tr_undo_discarded', { docNos: undone.join(', ') })}</div>}
      <div>
        <div style={sectionHead}>{t('aw_bank_tr_suggestions')}</div>
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          {data === null ? <Loader /> : !sugg.length ? <div style={emptyLine}>{t('aw_bank_tr_no_suggestions')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t('aw_col_date')}</th>
                  <th style={th}>{t('aw_bank_tr_from')}</th>
                  <th style={th}>{t('aw_bank_tr_to')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_tr_days')}</th>
                  {!ro && <th style={th}>{t('aw_col_actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {sugg.map(s => {
                  const key = `${s.debit.id}:${s.credit.id}`;
                  return (
                    <tr key={key}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {dmy(s.debit.txn_date)}
                        {s.credit.txn_date !== s.debit.txn_date && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>→ {dmy(s.credit.txn_date)}</div>}
                      </td>
                      <td style={td}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.debit.account_number}</span><div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{s.debit.description}</div></td>
                      <td style={td}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.credit.account_number}</span><div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{s.credit.description}</div></td>
                      <td style={{ ...num, fontWeight: 700 }}>{amt(s.amount)}</td>
                      <td style={num}>{s.days}</td>
                      {!ro && <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button onClick={() => confirmPair(s)} disabled={!!busy} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 12 }}>
                          <i className={busy === key ? 'ti ti-loader' : 'ti ti-arrows-exchange'} aria-hidden /> {t('aw_bank_tr_confirm')}
                        </button>
                      </td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_bank_tr_confirmed')}</div>
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          {data === null ? <Loader /> : !conf.length ? <div style={emptyLine}>{t('aw_bank_tr_no_confirmed')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t('aw_col_date')}</th>
                  <th style={th}>{t('aw_bank_tr_from')}</th>
                  <th style={th}>{t('aw_bank_tr_to')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                  <th style={th}>{t('aw_bank_tr_by')}</th>
                  <th style={th}>{t('aw_tr_settle_col')}</th>
                  {!ro && <th style={th}>{t('aw_col_actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {conf.map(c => (
                  <tr key={c.pair_id} data-tr-pair={c.pair_id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(c.date)}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{c.from_account_number}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{c.to_account_number}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{amt(c.amount)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.matched_by_name}{c.matched_at ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{fmtDateTime(c.matched_at)}</div> : null}</td>
                    <td style={td}><span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }} data-tr-cell={c.pair_id}>{settleCell(c)}{entriesOf(c)}</span></td>
                    {!ro && <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {/* Batch 11 R3 (核对 14): the pair's settlement (its debit line's) still in the books → the sentence
                          and "Reverse JV-…", never an Undo the server refuses. */}
                      {c.settlement?.inBooks
                        ? <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'normal', display: 'inline-flex', flexDirection: 'column', gap: 4, maxWidth: 280 }} data-ll-settled={c.settlement.docNo}>
                            <span>{c.settlement.reversalDraft
                              ? t('aw_ll_bank_settled_draft', { docNo: c.settlement.docNo, rv: c.settlement.reversalDraft })
                              : c.settlement.closed
                                ? t('aw_ll_transfer_settled_closed', { docNo: c.settlement.docNo, from: dmy(c.settlement.closed.start), to: dmy(c.settlement.closed.end) })
                                : t('aw_ll_transfer_settled', { docNo: c.settlement.docNo })}</span>
                            {!c.settlement.closed && <ReverseEntryBtn ctx={ctx} kind="bank_line" id={c.settlement.lineId || c.debit_line_id || ''} docNo={c.from_account_number} entry={c.settlement} style={{ ...actBtn, padding: '3px 8px', fontSize: 11.5 }} />}
                          </span>
                        : <button onClick={() => undo(c)} disabled={!!busy} style={actBtn}><i className={busy === c.pair_id ? 'ti ti-loader' : 'ti ti-arrow-back-up'} aria-hidden /> {t('aw_bank_tr_undo')}</button>}
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div style={note}>{t('aw_bank_tr_note')}</div>
      <div style={note} data-tr-settle-note>{t('aw_tr_settle_note')}</div>
      {acking && !!acking.debit_line_id && (
        <SettleAckDialog ctx={ctx} source={{ kind: 'bank_line', id: acking.debit_line_id }} line={{ date: acking.date, amount: acking.amount }} onClose={() => setAcking(null)}
          onDone={remaining => { if (!remaining) setAcking(null); ctx.bump(); }} />
      )}
    </div>
  );
}
